/**
 * Scarcity Engine - Advanced Dynamic Low Stock & Urgency System
 * 
 * Features:
 * 1. Advanced Stock Management with Decay (T+30s, T+24h, T+48h)
 * 2. Strict Color Prioritization (Checkout > Cart > Click > Load)
 * 3. Quantity Cap Logic
 * 4. Cart & Checkout Persistence
 * 
 * https://www.toptal.com/developers/javascript-minifier
 */

class ScarcityEngine {
    constructor() {
        this.STORAGE_KEY = 'AGB_ScarcityState';

        // ═══════════════════════════════════════════════════════════════
        // TRACKING SYSTEM CONFIGURATION
        // ═══════════════════════════════════════════════════════════════

        // Master switch - set to false to disable ALL tracking
        this.TRACKING_ENABLED = true;

        // Endpoint - leave empty '' to disable sending (events still queue locally)
        this.TRACKING_ENDPOINT = '';
        // this.TRACKING_ENDPOINT = 'https://database-app-2-lcae.onrender.com/low-stock/logs';


        // Transport settings
        this.TRACKING_QUEUE_KEY = 'AGB_ScarcityTrackingQueue';
        this.TRACKING_SESSION_ID_KEY = 'AGB_SESSION_ID';
        this.TRACKING_SESSION_START_KEY = 'AGB_SESSION_START';
        this.TRACKING_FLUSH_INTERVAL_MS = 15 * 1000; // flush every 15s
        this.TRACKING_BATCH_SIZE = 50;
        this.TRACKING_MAX_QUEUE = 1000;
        this.TRACKING_SCHEMA_VERSION = 1;

        // Console logging - set to true to log queued events to console
        this.TRACKING_CONSOLE_LOG = false;

        // Per-event toggles - set to false to suppress specific events
        this.TRACKING_EVENTS = {
            engine_initialized:                     true,
            state_saved:                            true,
            state_save_failed:                      true,
            favorite_color_updated:                 true,
            low_stock_exposed:                      true,
            variant_input_changed:                  true,
            product_page_loaded:                    true,
            variant_changed:                        true,
            cart_add_detected:                      true,
            swatch_clicked:                         true,
            cart_add_form_submitted:                true,
            fake_scarcity_properties_removed:       true,
            fake_scarcity_properties_applied:       true,
            checkout_clicked:                       true,
            checkout_low_stock_item_enriched:       true,
            checkout_low_stock_item_enrich_failed:  true,
            interaction_recorded:                   true,
            lifecycle_start_blocked:                true,
            lifecycle_started:                      true,
        };

        // ═══════════════════════════════════════════════════════════════
        // END TRACKING CONFIGURATION
        // ═══════════════════════════════════════════════════════════════
        this.PRIORITY_COLORS = ['Multicam', 'Ranger green', 'Black', 'Coyote Tan', 'Coyote Brown', 'Woodland', 'Multicam Black'];
        this.OOS_COLORS = ['Wolf Grey', 'Coyote Tan', 'Multicam Black', 'M81 Woodland', 'Coyote Brown', 'Black', 'Ranger green', 'Multicam'];
        this.MAX_ACTIVE_PRODUCTS = 4; // Maximum number of products that can have active scarcity simultaneously
        this.REAL_STOCK_THRESHOLD = 10; // Show real stock warning if inventory <= this value
        this.PATCH_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 2 weeks cooldown for patch promotion
        this.PATCH_TIMER_MS = 2 * 60 * 60 * 1000; // 2 hours timer duration for patch
        this.PATCH_PROMOTION_ENABLED = false; // TEMPORARILY DISABLED - Set to true to re-enable
        this.SWATCH_LIFECYCLE_MS = 14 * 24 * 60 * 60 * 1000; // 2 weeks lifecycle for artificial swatches

        // Independent Toggles
        // this.uiEnabled = Boolean(window.theme?.scarcityMode); // Fake scarcity system
        this.uiEnabled = true; // Fake scarcity system
        this.realStockEnabled = true; // Real low stock system (hardcoded toggle)
        this.artificialSwatchesEnabled = false; // Artificial swatches system (fake OOS swatches)

        this.activeColors = {};
        this.lineLimits = {};
        this.isAddingPatch = false; // Flag to prevent concurrent patch additions
        this.pdpLowStockState = {}; // handle -> { mode: 'fake' | 'real', variantId, count, updatedAt }
        this.isCheckoutFinalizing = false;
        this.trackingFlushIntervalId = null;
        this.isTrackingFlushInProgress = false;
        this.lowStockExposureSignatures = {};
        this.lastFavoriteColorByHandle = {};

        // Priority Levels (Higher is better)
        this.PRIORITIES = {
            'checkout': 100,
            'cart': 50,
            'click': 10,
            'load': 1
        };

        this.state = this.loadState();
        this.init();
    }

    init() {
        this.bindEvents();
        this.startTrackingTransport();
        this.queueTrackingEvent('engine_initialized', {
            uiEnabled: this.uiEnabled,
            realStockEnabled: this.realStockEnabled,
            artificialSwatchesEnabled: this.artificialSwatchesEnabled,
            maxActiveProducts: this.MAX_ACTIVE_PRODUCTS,
            realStockThreshold: this.REAL_STOCK_THRESHOLD
        });

        this.handleProductPageLoad();

        // Initialize free patch promotion timer if active
        this.initPatchPromotion();

        // Clean up expired swatch lifecycles
        this.checkAndCleanupSwatchLifecycles();

        // Start UI updates if EITHER system is enabled
        if (this.uiEnabled || this.realStockEnabled) {
            this.startDecayTimer();
            this.initUI();
        }
    }

    loadState() {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            const state = saved ? JSON.parse(saved) : {
                products: {}, // handle -> { stock, timestamp }
                interactions: {}, // handle -> { color -> { type, timestamp, weight } }
                favoriteColor: null,
                freePatchPromotion: null, // { addedAt, patchLineKey, isActive }
                artificialSwatches: {} // { [handle]: { addedAt, expiresAt } }
            };
            
            // Ensure artificialSwatches exists (for backward compatibility)
            if (!state.artificialSwatches) {
                state.artificialSwatches = {};
            }
            
            return state;
        } catch (e) {
            return { products: {}, interactions: {}, favoriteColor: null, freePatchPromotion: null, artificialSwatches: {} };
        }
    }

    saveState(reason = 'state_update', context = null) {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state));
            this.queueTrackingEvent('state_saved', {
                reason,
                context,
                stateSummary: this.getTrackingStateSummary()
            });
            return true;
        } catch (e) {
            // console.warn('ScarcityEngine: Storage full');
            this.queueTrackingEvent('state_save_failed', {
                reason,
                error: String(e?.message || e || 'unknown_error')
            });
            return false;
        }
    }

    startTrackingTransport() {
        if (this.trackingFlushIntervalId) return;

        this.trackingFlushIntervalId = setInterval(() => {
            this.flushTrackingQueue();
        }, this.TRACKING_FLUSH_INTERVAL_MS);

        window.addEventListener('pagehide', () => {
            this.flushTrackingQueue({ useBeacon: true });
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.flushTrackingQueue({ useBeacon: true });
            }
        });

        window.addEventListener('beforeunload', () => {
            this.flushTrackingQueue({ useBeacon: true });
        });

        window.addEventListener('online', () => {
            this.flushTrackingQueue();
        });
    }

    getTrackingEndpoint() {
        return this.TRACKING_ENDPOINT || '';
    }

    createTrackingId() {
        try {
            if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
            }
        } catch (e) {
            // Fallback below
        }
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    loadTrackingQueue() {
        try {
            const raw = localStorage.getItem(this.TRACKING_QUEUE_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    saveTrackingQueue(events) {
        try {
            localStorage.setItem(this.TRACKING_QUEUE_KEY, JSON.stringify(Array.isArray(events) ? events : []));
        } catch (e) {
            // Best effort only
        }
    }

    getCookie(name) {
        if (!name) return null;
        const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
        const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
    }

    getViewerId() {
        try {
            const fromLocalStorage = localStorage.getItem('viewerId') || localStorage.getItem('visitorID');
            if (fromLocalStorage) return fromLocalStorage;
        } catch (e) {
            // Ignore localStorage read errors
        }

        return this.getCookie('viewerId') || this.getCookie('visitorID') || null;
    }

    getTrackingSessionId() {
        try {
            let sessionId = sessionStorage.getItem(this.TRACKING_SESSION_ID_KEY);
            if (!sessionId) {
                sessionId = this.createTrackingId();
                sessionStorage.setItem(this.TRACKING_SESSION_ID_KEY, sessionId);
            }
            return sessionId;
        } catch (e) {
            return this.createTrackingId();
        }
    }

    getTrackingSessionStartMs() {
        try {
            let startTime = sessionStorage.getItem(this.TRACKING_SESSION_START_KEY);
            if (!startTime) {
                startTime = String(Date.now());
                sessionStorage.setItem(this.TRACKING_SESSION_START_KEY, startTime);
            }
            const parsed = parseInt(startTime, 10);
            return Number.isFinite(parsed) ? parsed : Date.now();
        } catch (e) {
            return Date.now();
        }
    }

    sanitizeTrackingPayload(value) {
        if (value === undefined) return undefined;
        if (value === null) return null;
        if (typeof value === 'function') return undefined;

        if (typeof Element !== 'undefined' && value instanceof Element) {
            return undefined;
        }

        if (value instanceof Date) {
            return value.toISOString();
        }

        if (Array.isArray(value)) {
            return value
                .map(item => this.sanitizeTrackingPayload(item))
                .filter(item => item !== undefined);
        }

        if (typeof value === 'object') {
            const out = {};
            Object.entries(value).forEach(([key, item]) => {
                if (key === 'artificialSwatches' || key === 'freePatchPromotion') return;
                const sanitized = this.sanitizeTrackingPayload(item);
                if (sanitized !== undefined) {
                    out[key] = sanitized;
                }
            });
            return out;
        }

        if (typeof value === 'number' && !Number.isFinite(value)) {
            return null;
        }

        return value;
    }

    getTrackingStateSnapshot() {
        return this.sanitizeTrackingPayload({
            products: this.state?.products || {},
            interactions: this.state?.interactions || {},
            favoriteColor: this.state?.favoriteColor || null,
            lifecycles: this.state?.lifecycles || {},
            activeColors: this.activeColors || {},
            pdpLowStockState: this.pdpLowStockState || {}
        });
    }

    getTrackingStateSummary() {
        const snapshot = this.getTrackingStateSnapshot();
        return {
            productsCount: Object.keys(snapshot.products || {}).length,
            interactionProductsCount: Object.keys(snapshot.interactions || {}).length,
            lifecycleCount: Object.keys(snapshot.lifecycles || {}).length,
            activeColorProductsCount: Object.keys(snapshot.activeColors || {}).length,
            pdpLowStockProductsCount: Object.keys(snapshot.pdpLowStockState || {}).length,
            favoriteColor: snapshot.favoriteColor || null
        };
    }

    queueTrackingEvent(eventName, detail = {}, options = {}) {
        try {
            // Master switch
            if (!this.TRACKING_ENABLED) return;

            // Per-event toggle (unknown events are allowed by default)
            if (this.TRACKING_EVENTS.hasOwnProperty(eventName) && !this.TRACKING_EVENTS[eventName]) return;

            const queue = this.loadTrackingQueue();

            const event = {
                eventId: this.createTrackingId(),
                eventName: String(eventName || 'unknown'),
                createdAt: new Date().toISOString(),
                createdAtMs: Date.now(),
                sessionId: this.getTrackingSessionId(),
                viewerId: this.getViewerId(),
                page: {
                    path: window.location.pathname,
                    search: window.location.search,
                    title: document.title || null
                },
                detail: this.sanitizeTrackingPayload(detail)
            };

            if (this.TRACKING_CONSOLE_LOG) {
                console.log(`[ScarcityTracking] ${eventName}`, event);
            }

            queue.push(event);

            if (queue.length > this.TRACKING_MAX_QUEUE) {
                queue.splice(0, queue.length - this.TRACKING_MAX_QUEUE);
            }

            this.saveTrackingQueue(queue);

            if (options.flushNow || queue.length >= this.TRACKING_BATCH_SIZE) {
                this.flushTrackingQueue();
            }
        } catch (e) {
            // Best effort only
        }
    }

    buildTrackingPayload(events) {
        return {
            type: 'scarcity_low_stock_tracking_pulse',
            schemaVersion: this.TRACKING_SCHEMA_VERSION,
            sentAt: new Date().toISOString(),
            sessionId: this.getTrackingSessionId(),
            sessionStartedAt: new Date(this.getTrackingSessionStartMs()).toISOString(),
            viewerId: this.getViewerId(),
            site: window.location.host,
            path: window.location.pathname + window.location.search,
            referrer: document.referrer || null,
            state: this.getTrackingStateSnapshot(),
            events: this.sanitizeTrackingPayload(events)
        };
    }

    removeTrackingEventsById(events) {
        if (!Array.isArray(events) || events.length === 0) return;
        const ids = new Set(events.map(event => event?.eventId).filter(Boolean));
        if (!ids.size) return;

        const currentQueue = this.loadTrackingQueue();
        const nextQueue = currentQueue.filter(event => !ids.has(event?.eventId));
        this.saveTrackingQueue(nextQueue);
    }

    async flushTrackingQueue({ useBeacon = false, force = false } = {}) {
        if (this.isTrackingFlushInProgress && !force) return false;

        const endpoint = this.getTrackingEndpoint();
        if (!endpoint) return false;

        const queue = this.loadTrackingQueue();
        if (!queue.length) return false;

        const batch = queue.slice(0, this.TRACKING_BATCH_SIZE);
        const body = JSON.stringify(this.buildTrackingPayload(batch));
        console.log(this.buildTrackingPayload(batch));
        

        if (useBeacon && navigator.sendBeacon) {
            try {
                const ok = navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
                if (ok) {
                    // console.log('sent');
                    
                    this.removeTrackingEventsById(batch);
                    return true;
                }
            } catch (e) {
                // console.log('didnt sent');
                
                // Fall through to normal fetch path
            }
        }

        this.isTrackingFlushInProgress = true;
        try {
            // console.log('tttttt');
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body,
                credentials: 'omit',
                mode: 'cors'
            });

            if (!response.ok) {
                return false;
            }
            // console.log(batch);
            
            this.removeTrackingEventsById(batch);

            if (this.loadTrackingQueue().length >= this.TRACKING_BATCH_SIZE) {
                setTimeout(() => {
                    this.flushTrackingQueue();
                }, 100);
            }

            return true;
        } catch (e) {
            return false;
        } finally {
            this.isTrackingFlushInProgress = false;
        }
    }

    getTrackingStats() {
        return {
            queueLength: this.loadTrackingQueue().length,
            flushIntervalMs: this.TRACKING_FLUSH_INTERVAL_MS,
            endpointConfigured: Boolean(this.getTrackingEndpoint()),
            sessionId: this.getTrackingSessionId(),
            viewerId: this.getViewerId()
        };
    }

    maybeTrackFavoriteColor(handle, source = 'unknown') {
        if (!handle) return;
        const favoriteColor = this.getBestColor(handle);
        if (!favoriteColor) return;

        if (this.lastFavoriteColorByHandle[handle] === favoriteColor) return;
        this.lastFavoriteColorByHandle[handle] = favoriteColor;

        this.queueTrackingEvent('favorite_color_updated', {
            handle,
            favoriteColor,
            source
        });
    }

    trackLowStockExposure(handle, mode, data = {}) {
        if (!handle) return;
        const signature = `${mode}:${data.variantId || ''}:${data.count || ''}:${data.variantOptions || ''}`;
        if (this.lowStockExposureSignatures[handle] === signature) return;
        this.lowStockExposureSignatures[handle] = signature;

        this.queueTrackingEvent('low_stock_exposed', {
            handle,
            mode,
            ...data
        });
    }

    bindEvents() {
        // Variant Change (Load/View)
        document.addEventListener('variant:change', (e) => this.handleVariantChange(e));

        // Robustness: Listen for direct input changes in variant pickers
        document.addEventListener('change', (e) => {
            if (e.target.matches('variant-picker input[type="radio"], variant-picker select')) {
                const picker = e.target.closest('variant-picker');
                if (picker) {
                    this.queueTrackingEvent('variant_input_changed', {
                        handle: picker.getAttribute('handle') || null,
                        inputName: e.target.name || null,
                        value: e.target.value || null,
                        inputType: e.target.type || e.target.tagName?.toLowerCase() || null
                    });

                    // Small delay to allow DOM/State updates
                    setTimeout(() => {
                        this.refreshUI(picker.getAttribute('handle'));
                    }, 50);
                }
            }
        });

        // Cart Add
        document.addEventListener('variant:add', (e) => this.handleCartAdd(e));

        // UI Interactions (Click) & Checkout tracking
        document.addEventListener('click', (e) => {
            this.handleClicks(e);
            this.handleCheckoutClick(e);
        });

        // Global form submissions for cart add tracking
        document.addEventListener('submit', (e) => {
            this.handleFormSubmit(e);
            this.handleCartChangeForm(e);
        });

        // Intercept AJAX Cart Adds (Fetch) - DISABLED: Use document events instead
        // this.bindAjaxInterceptors();

        // Global Cart Updates
        window.addEventListener('storage', (e) => {
            if (e.key === this.STORAGE_KEY) {
                this.state = this.loadState();
                this.refreshUI();
            }
        });

        // Listen for cart change events from theme
        document.addEventListener('cart:change', () => {
            // Refresh cart items after cart changes to update limits
            setTimeout(() => {
                this.refreshCartItems();
            }, 100);
        });

        document.addEventListener('line-item:change', (e) => {
            // Refresh cart items after line item changes
            setTimeout(() => {
                this.refreshCartItems();
            }, 100);

            // Check if patch was manually removed
            if (e.detail && e.detail.quantity === 0) {
                const lineKey = e.target?.closest('line-item')?.querySelector('input[data-line-key]')?.dataset?.lineKey;
                if (lineKey && this.state.freePatchPromotion && this.state.freePatchPromotion.patchLineKey === lineKey) {
                    // Patch was manually removed, clear promotion
                    this.state.freePatchPromotion = null;
                    this.saveState();
                    if (this.patchTimerInterval) {
                        clearInterval(this.patchTimerInterval);
                        this.patchTimerInterval = null;
                    }
                }
            }
        });

        // Intercept line-item:will-change to clamp quantity before it's sent
        document.addEventListener('line-item:will-change', (e) => {
            const lineKey = e.detail?.lineKey || e.target?.closest('line-item')?.querySelector('input[data-line-key]')?.dataset?.lineKey;
            const targetQuantity = e.detail?.targetQuantity;

            if (lineKey && targetQuantity !== undefined) {
                const limit = this.getLimitForLine(lineKey);
                if (limit && targetQuantity > limit) {
                    // Clamp the quantity in the event detail
                    e.detail.targetQuantity = limit;

                    // Also update the input value
                    const input = document.querySelector(`input[data-line-key="${lineKey}"]`);
                    if (input) {
                        input.value = limit;
                    }
                }
            }
        });

        // Bundle resolution events - refresh bundle low stock when bundle components change
        document.addEventListener('baseItemChanged', () => {
            const picker = document.querySelector('variant-picker');
            if (picker) {
                const handle = picker.getAttribute('handle');
                if (handle && this.isBundleProduct(handle)) {
                    setTimeout(() => {
                        this.refreshUI(handle);
                    }, 100); // Small delay to allow bundle resolution to complete
                }
            }
        });

        document.addEventListener('acceptedItemsChanged', () => {
            const picker = document.querySelector('variant-picker');
            if (picker) {
                const handle = picker.getAttribute('handle');
                if (handle && this.isBundleProduct(handle)) {
                    setTimeout(() => {
                        this.refreshUI(handle);
                    }, 100); // Small delay to allow bundle resolution to complete
                }
            }
        });

        // Quantity input enforcement - prevent manual entry over limit
        this.bindQuantityInputEnforcement();

        // Intercept plus button clicks
        document.addEventListener('click', (e) => {
            this.handleQuantityButtonClick(e);
        });

        // Intercept URL navigation for cart changes
        this.interceptCartChangeNavigation();
    }

    // DISABLED: Global fetch monkey-patch removed per user request
    // ScarcityEngine now uses document event listeners instead (cart:change, line-item:change, submit)
    // bindAjaxInterceptors() {
    //     const originalFetch = window.fetch;
    //     window.fetch = async (...args) => {
    //         let [url, config] = args;

    //         // Check if it's a cart add request
    //         if (url && String(url).includes('/cart/add')) {
    //             try {
    //                 this.handleAjaxCartAdd(config);
    //             } catch (e) {
    //                 // console.error('ScarcityEngine: Error interception fetch', e);
    //             }
    //         }
    //         if (url && String(url).includes('/cart/change')) {
    //             try {
    //                 config = await this.interceptCartChange(config);
    //                 args[1] = config; // Update config in args array
    //             } catch (e) { }
    //         }

    //         return originalFetch.apply(window, args);
    //     };
    // }

    async interceptCartChange(config) {
        if (!config) return config;
        const headers = config.headers || {};
        const contentType = headers['Content-Type'] || headers['content-type'] || '';
        let body = config.body;
        if (!body) return config;

        if (contentType.includes('application/x-www-form-urlencoded')) {
            const params = new URLSearchParams(body.toString());
            this.clampCartChangeParams(params);
            config = { ...config, body: params.toString() };
        } else if (contentType.includes('application/json')) {
            try {
                const json = typeof body === 'string' ? JSON.parse(body) : body;
                if (json && typeof json === 'object') {
                    this.clampCartChangeJson(json);
                    config = { ...config, body: JSON.stringify(json) };
                }
            } catch (e) {
                // console.error('ScarcityEngine: failed to parse cart change body', e);
            }
        }
        return config;
    }

    clampCartChangeParams(params) {
        const lineKey = params.get('id');
        const quantity = Number(params.get('quantity'));
        const limit = this.getLimitForLine(lineKey);
        if (limit && quantity > limit) {
            params.set('quantity', String(limit));
        }
    }

    clampCartChangeJson(json) {
        // Handle both single item and items array formats
        if (json && Array.isArray(json.items)) {
            json.items = json.items.map(item => {
                const limit = this.getLimitForLine(item.id);
                if (limit && item.quantity > limit) {
                    item.quantity = limit;
                }
                return item;
            });
        } else if (json && json.id && json.quantity !== undefined) {
            // Single item format: { id: 'lineKey', quantity: 5 }
            const limit = this.getLimitForLine(json.id);
            if (limit && json.quantity > limit) {
                json.quantity = limit;
            }
        }
    }

    handleCartChangeForm(event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/change')) return;
        const input = form.querySelector('input[name="quantity"]');
        const lineKey = form.querySelector('input[name="id"]')?.value;
        const limit = this.getLimitForLine(lineKey);
        if (input && limit) {
            const qty = parseInt(input.value, 10);
            if (qty > limit) {
                input.value = limit;
            }
        }
    }

    getLimitForLine(lineKey) {
        return lineKey ? this.lineLimits[lineKey] || null : null;
    }

    /**
     * Bind event listeners to quantity inputs to enforce hard limits
     */
    bindQuantityInputEnforcement() {
        // Use event delegation for dynamically added inputs
        document.addEventListener('input', (e) => {
            if (e.target.matches('quantity-selector input[type="number"], input[data-line-key]')) {
                this.enforceQuantityInputLimit(e.target);
            }
        });

        document.addEventListener('beforeinput', (e) => {
            if (e.target.matches('quantity-selector input[type="number"], input[data-line-key]')) {
                this.preventInvalidQuantityInput(e);
            }
        });

        document.addEventListener('blur', (e) => {
            if (e.target.matches('quantity-selector input[type="number"], input[data-line-key]')) {
                this.clampQuantityInputValue(e.target);
            }
        }, true);

        document.addEventListener('keydown', (e) => {
            if (e.target.matches('quantity-selector input[type="number"], input[data-line-key]')) {
                this.handleQuantityInputKeydown(e);
            }
        });

        // Also bind to existing inputs on page load
        this.attachQuantityInputListeners();
    }

    /**
     * Attach listeners to existing quantity inputs
     */
    attachQuantityInputListeners() {
        document.querySelectorAll('quantity-selector input[type="number"], input[data-line-key]').forEach(input => {
            // Prevent paste of invalid values
            input.addEventListener('paste', (e) => {
                e.preventDefault();
                const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                const pastedValue = parseInt(pastedText, 10);
                if (!isNaN(pastedValue)) {
                    const lineKey = input.dataset.lineKey;
                    const limit = this.getLimitForLine(lineKey);
                    if (limit && pastedValue > limit) {
                        input.value = limit;
                    } else {
                        input.value = pastedValue;
                    }
                    this.enforceQuantityInputLimit(input);
                }
            });
        });
    }

    /**
     * Enforce limit on quantity input value
     */
    enforceQuantityInputLimit(input) {
        const lineKey = input.dataset.lineKey;
        if (!lineKey) return;

        const limit = this.getLimitForLine(lineKey);
        if (!limit) return;

        const currentValue = parseInt(input.value, 10);
        if (isNaN(currentValue) || currentValue < 1) {
            input.value = 1;
            return;
        }

        if (currentValue > limit) {
            input.value = limit;
            // Dispatch change event to trigger cart update with clamped value
            input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        }
    }

    /**
     * Prevent invalid input before it's entered
     */
    preventInvalidQuantityInput(e) {
        const input = e.target;
        const lineKey = input.dataset.lineKey;
        if (!lineKey) return;

        const limit = this.getLimitForLine(lineKey);
        if (!limit) return;

        const inputType = e.inputType;
        if (inputType === 'insertText' || inputType === 'insertCompositionText') {
            const newText = input.value.slice(0, input.selectionStart) + e.data + input.value.slice(input.selectionEnd);
            const newValue = parseInt(newText, 10);
            if (!isNaN(newValue) && newValue > limit) {
                e.preventDefault();
                input.value = limit;
                input.setSelectionRange(String(limit).length, String(limit).length);
            }
        }
    }

    /**
     * Clamp quantity input value on blur
     */
    clampQuantityInputValue(input) {
        const lineKey = input.dataset.lineKey;
        if (!lineKey) return;

        const limit = this.getLimitForLine(lineKey);
        if (!limit) return;

        const currentValue = parseInt(input.value, 10);
        if (isNaN(currentValue) || currentValue < 1) {
            input.value = 1;
        } else if (currentValue > limit) {
            input.value = limit;
            // Trigger change to update cart
            input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        }
    }

    /**
     * Handle arrow key navigation in quantity input
     */
    handleQuantityInputKeydown(e) {
        const input = e.target;
        const lineKey = input.dataset.lineKey;
        if (!lineKey) return;

        const limit = this.getLimitForLine(lineKey);
        if (!limit) return;

        const currentValue = parseInt(input.value, 10) || 1;

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            let newValue = currentValue;
            if (e.key === 'ArrowUp') {
                newValue = Math.min(currentValue + 1, limit);
            } else {
                newValue = Math.max(currentValue - 1, 1);
            }
            input.value = newValue;
            input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        }
    }

    /**
     * Intercept plus button clicks and prevent navigation if over limit
     */
    handleQuantityButtonClick(e) {
        const button = e.target.closest('.quantity-selector__button');
        if (!button) return;

        const href = button.getAttribute('href');
        if (!href || !href.includes('/cart/change')) return;

        // Parse URL to get line key and quantity
        try {
            const url = new URL(href, window.location.origin);
            const lineKey = url.searchParams.get('id');
            const quantity = parseInt(url.searchParams.get('quantity'), 10);

            if (!lineKey || isNaN(quantity)) return;

            const limit = this.getLimitForLine(lineKey);
            if (limit && quantity > limit) {
                e.preventDefault();
                e.stopPropagation();

                // Update the input value to the limit
                const input = document.querySelector(`input[data-line-key="${lineKey}"]`);
                if (input) {
                    input.value = limit;
                    input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
                }

                return false;
            }
        } catch (err) {
            // Invalid URL, let it proceed normally
        }
    }

    /**
     * Intercept direct URL navigation to /cart/change
     */
    interceptCartChangeNavigation() {
        // Intercept before navigation
        window.addEventListener('beforeunload', (e) => {
            // Check if we're navigating to a cart change URL
            const currentUrl = window.location.href;
            if (currentUrl.includes('/cart/change')) {
                try {
                    const url = new URL(currentUrl);
                    const lineKey = url.searchParams.get('id');
                    const quantity = parseInt(url.searchParams.get('quantity'), 10);

                    if (lineKey && !isNaN(quantity)) {
                        const limit = this.getLimitForLine(lineKey);
                        if (limit && quantity > limit) {
                            // Modify URL before navigation
                            url.searchParams.set('quantity', String(limit));
                            window.history.replaceState({}, '', url.toString());
                        }
                    }
                } catch (err) {
                    // Ignore errors
                }
            }
        });

        // Also intercept link clicks that might navigate
        document.addEventListener('click', (e) => {
            const link = e.target.closest('a[href*="/cart/change"]');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href) return;

            try {
                const url = new URL(href, window.location.origin);
                const lineKey = url.searchParams.get('id');
                const quantity = parseInt(url.searchParams.get('quantity'), 10);

                if (lineKey && !isNaN(quantity)) {
                    const limit = this.getLimitForLine(lineKey);
                    if (limit && quantity > limit) {
                        e.preventDefault();
                        e.stopPropagation();

                        // Update URL and navigate
                        url.searchParams.set('quantity', String(limit));
                        window.location.href = url.toString();
                        return false;
                    }
                }
            } catch (err) {
                // Invalid URL, let it proceed normally
            }
        }, true);
    }

    handleAjaxCartAdd(config) {
        if (!config || !config.body) {
            return;
        }

        let data;
        try {
            data = JSON.parse(config.body);
        } catch (e) {
            return;
        }

        // Support both single item and 'items' array format
        const items = data.items || [data];

        items.forEach(item => {
            if (item.id) {
                // We have variant ID, need to resolve handle and color
                // We can try to find the product handle via the page or stored maps
                // For AGB, we are usually on the product page, so we can use current page handle
                // if the variant ID belongs to the current product.

                this.resolveVariantAndRecord(item.id, 'cart');
            }
        });

        // Check if we should trigger patch promotion after cart add
        // This handles the case where lifecycle was created before cart add
        this.checkAndTriggerPatchPromotionOnCartAdd();
    }

    /**
     * Check if patch promotion should be triggered after cart add
     * This ensures promotion triggers even if lifecycle was created before cart add
     */
    async checkAndTriggerPatchPromotionOnCartAdd() {
        // TEMPORARILY DISABLED
        if (!this.PATCH_PROMOTION_ENABLED) {
            return;
        }

        // Check if promotion exists and if cooldown is still active
        if (this.state.freePatchPromotion) {
            // Check if promotion is currently active
            if (this.state.freePatchPromotion.isActive) {
                return; // Already active, don't add again
            }

            // Check if cooldown is still active
            if (this.state.freePatchPromotion.cooldownUntil) {
                const now = Date.now();
                if (now < this.state.freePatchPromotion.cooldownUntil) {
                    return; // Cooldown still active
                } else {
                    // Cooldown expired, clear old state to allow new promotion
                    this.state.freePatchPromotion = null;
                    this.saveState();
                }
            } else {
                // No cooldown set, but promotion exists - likely old state, clear it
                this.state.freePatchPromotion = null;
                this.saveState();
            }
        }

        // Only trigger on first lifecycle (activeCount === 1)
        const activeCount = this.countActiveLifecycles();

        if (activeCount === 1) {
            // Check if patch is already in cart
            const cart = await this.getCartData();
            if (cart && cart.items) {
                const patchInCart = cart.items.find(item =>
                    item.product_handle === '4th-of-july-us-scorpion-patch'
                );
                if (!patchInCart) {
                    // Patch not in cart, trigger promotion
                    this.triggerFreePatchPromotion().catch(e => {
                        // Error triggering patch promotion
                    });
                } else {
                    // Patch already in cart, just activate promotion tracking with cooldown
                    this.state.freePatchPromotion = {
                        addedAt: Date.now(),
                        patchLineKey: patchInCart.key,
                        isActive: true,
                        cooldownUntil: Date.now() + this.PATCH_COOLDOWN_MS // 2 weeks from now
                    };
                    this.saveState();
                    this.startPatchTimer();
                }
            }
        }
    }

    async resolveVariantAndRecord(variantId, type) {
        // 1. Try to find variant in current page's picker data
        const pickers = document.querySelectorAll('variant-picker');
        for (const picker of pickers) {
            const handle = picker.getAttribute('handle');
            const json = picker.querySelector('[data-product-variants-json]');
            if (json) {
                try {
                    const productData = JSON.parse(json.textContent);
                    const variant = productData.variants.find(v => String(v.id) === String(variantId));
                    if (variant) {
                        const color = this.getVariantColor(variant);
                        if (handle && color) {
                            this.recordInteraction(handle, color, type);
                            if (this.uiEnabled) this.refreshUI(handle);
                            return;
                        }
                    }
                } catch (e) { }
            }
        }

        // 2. If not found on page (e.g. quick add for another product), we might skip
        // or we could fetch product data if we really needed to.
        // For AGB, the variant SHOULD be on the current page.
    }

    handleProductPageLoad() {
        const picker = document.querySelector('variant-picker');
        if (!picker) return;

        const handle = picker.getAttribute('handle');
        if (!handle) return;

        // Record initial load for current color if available
        const currentVariantId = document.querySelector('input[name="id"]')?.value;
        this.queueTrackingEvent('product_page_loaded', {
            handle,
            currentVariantId: currentVariantId || null
        });

        if (currentVariantId) {
            // Need product data to resolve ID to color
            const json = picker.querySelector('[data-product-variants-json]');
            if (json) {
                try {
                    const data = JSON.parse(json.textContent);
                    const variant = data.variants.find(v => String(v.id) === String(currentVariantId));
                    const color = this.getVariantColor(variant);
                    if (color) {
                        this.recordInteraction(handle, color, 'load');
                    }
                } catch (e) { }
            }
        }

        this.maybeTrackFavoriteColor(handle, 'product_page_load');

        // Note: We don't fix swatch availability states here - the Liquid template handles this correctly
        // Our fixSwatchAvailabilityStates() method was causing conflicts with the template's logic
        
        if (this.uiEnabled || this.realStockEnabled) {
            this.refreshUI(handle);
        } else {
            // Even if UI is disabled, we should still add artificial swatches on initial load
            // But only if artificial swatches are enabled AND product has an active low stock lifecycle
            if (this.artificialSwatchesEnabled) {
                const favoriteColor = this.getBestColor(handle);
                const productHasActiveLifecycle = this.countActiveLifecyclesForProduct(handle) > 0;
                const hasValidSwatchLifecycle = this.isSwatchLifecycleValid(handle);
                
                if (favoriteColor && productHasActiveLifecycle && picker) {
                    // If swatch lifecycle is valid or doesn't exist yet, add swatches
                    // (addArtificialSwatches will create lifecycle if needed)
                    if (hasValidSwatchLifecycle || !this.state.artificialSwatches || !this.state.artificialSwatches[handle]) {
                        this.addArtificialSwatches(picker, favoriteColor);
                    } else {
                        // Lifecycle expired - remove swatches and clean up
                        this.removeArtificialSwatches(picker);
                        delete this.state.artificialSwatches[handle];
                        this.saveState();
                    }
                } else {
                    // Remove artificial swatches if no favorite color or no active lifecycle
                    if (picker) {
                        this.removeArtificialSwatches(picker);
                    }
                }
            } else {
                // Artificial swatches disabled - remove any existing ones
                if (picker) {
                    this.removeArtificialSwatches(picker);
                }
            }
        }
    }

    handleVariantChange(e) {
        const { variant, product } = e.detail;
        if (!variant || !product) return;

        const color = this.getVariantColor(variant);
        this.queueTrackingEvent('variant_changed', {
            handle: product.handle || null,
            variantId: variant.id ? String(variant.id) : null,
            color: color || null,
            available: variant.available === true,
            inventoryQuantity: Number.isFinite(variant.inventory_quantity) ? variant.inventory_quantity : null
        });

        if (color) {
            // View interaction
            this.recordInteraction(product.handle, color, 'load');
        }

        if (this.uiEnabled || this.realStockEnabled) {
            this.refreshUI(product.handle);
        }
    }

    async handleCartAdd(e) {
        // Assuming event detail contains product handle/variant info
        const detail = e.detail || {};

        // If detail has direct info, use it
        if (detail.handle && detail.color) {
            this.queueTrackingEvent('cart_add_detected', {
                source: 'variant_add_event_detail',
                handle: detail.handle,
                color: detail.color,
                variantId: detail.variantId ? String(detail.variantId) : null
            });
            this.recordInteraction(detail.handle, detail.color, 'cart');
            if (this.uiEnabled) this.refreshUI(detail.handle);
            // Check for patch promotion after cart add
            setTimeout(() => this.checkAndTriggerPatchPromotionOnCartAdd(), 500);
            return;
        }

        // Fallback: check active form
        const form = document.querySelector('form[action*="/cart/add"]');
        if (form) {
            const handle = this.getFormHandle(form);
            const color = this.getColorFromForm(form, handle);

            if (handle && color) {
                this.queueTrackingEvent('cart_add_detected', {
                    source: 'variant_add_form_fallback',
                    handle,
                    color,
                    variantId: form.querySelector('input[name="id"]')?.value || null
                });
                this.recordInteraction(handle, color, 'cart');
                if (this.uiEnabled) {
                    this.refreshUI(handle);
                }
                // Check for patch promotion after cart add
                setTimeout(() => this.checkAndTriggerPatchPromotionOnCartAdd(), 500);
            }
        }
    }

    handleClicks(e) {
        // Delegate clicks for AGB swatches first
        const customSwatch = e.target.closest('.variant-picker__swatch-wrapper');
        if (customSwatch) {
            this.captureSwatchInteraction(customSwatch, 'click');
            return;
        }

        // Fallback to the default picker markup (standard theme swatches)
        // Matches label.color-swatch or elements with data-option-value
        const defaultSwatch = e.target.closest('.color-swatch, [data-option-value]');
        if (defaultSwatch && defaultSwatch.closest('variant-picker')) {
            this.captureSwatchInteraction(defaultSwatch, 'click');
        }
    }

    captureSwatchInteraction(element, type) {
        // Try multiple sources for color name
        let color = element.dataset.colorValue ||
            element.getAttribute('value') ||
            element.dataset.optionValue;

        // If empty, try SR-only span or title/aria-label (common in standard themes)
        if (!color) {
            const srSpan = element.querySelector('.sr-only');
            if (srSpan) color = srSpan.textContent.trim();
        }

        if (!color) {
            color = element.getAttribute('title') || element.getAttribute('aria-label');
        }

        // If input ID is available in 'for' attribute, we might parse it, but text is safer

        const picker = element.closest('variant-picker');
        const handle = picker?.getAttribute('handle');

        if (handle && color) {
            this.queueTrackingEvent('swatch_clicked', {
                handle,
                color,
                interactionType: type
            });
            this.recordInteraction(handle, color, type);
            if (this.uiEnabled) {
                this.refreshUI(handle);
            }
        }
    }

    handleFormSubmit(e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;

        const action = (form.action || '').toLowerCase();
        if (!action.includes('/cart/add')) return;

        const handle = this.getFormHandle(form);
        const color = this.getColorFromForm(form, handle);
        const variantId = form.querySelector('input[name="id"]')?.value || null;

        if (handle && color) {
            this.queueTrackingEvent('cart_add_form_submitted', {
                handle,
                color,
                variantId
            });
            this.recordInteraction(handle, color, 'cart');
        }

        // Ensure fake scarcity properties are set on the form before submission
        this.setFakeScarcityPropertiesOnForm(form);
    }

    /**
     * Set fake scarcity properties on form before submission
     * This ensures the properties are included in the order details
     * @param {HTMLFormElement} form - The form element being submitted
     */
    setFakeScarcityPropertiesOnForm(form) {
        if (!form) return;

        const context = this.getActiveLowStockTrackingContextForForm(form);
        if (!context) {
            this.removeLowStockTrackingInputs(form);
            this.queueTrackingEvent('fake_scarcity_properties_removed', {
                reason: 'no_active_low_stock_context'
            });
            return;
        }

        this.queueTrackingEvent('fake_scarcity_properties_applied', {
            handle: context.handle,
            variantId: String(context.variantId),
            leftOnAddToCart: context.count
        });

        this.applyLowStockTrackingPropertiesToForm(form, {
            _isLowStockOnAddToCart: '1',
            _leftOnAddToCart: String(context.count),
            _lowStockVariantId: String(context.variantId),
            _lowStockHandle: context.handle,
            _lowStockAddedAtMs: String(Date.now())
        });
    }

    handleCheckoutClick(e) {
        const checkoutTrigger = e.target.closest(
            'a[href*="/checkout"], button[name="checkout"], [data-checkout]'
        );
        if (!checkoutTrigger) return;

        if (this.isCheckoutFinalizing) return;
        e.preventDefault();
        e.stopPropagation();

        const form = checkoutTrigger.closest('form');
        const handle = form ? this.getFormHandle(form) : this.getPageHandle();
        const color = form ? this.getColorFromForm(form, handle) : this.activeColors[handle];
        const variantId = form?.querySelector('input[name="id"]')?.value || null;

        this.queueTrackingEvent('checkout_clicked', {
            handle: handle || null,
            color: color || null,
            variantId
        });

        if (handle && color) {
            this.recordInteraction(handle, color, 'checkout');
        }

        this.finalizeCheckoutLowStockAndContinue(checkoutTrigger, form);
    }

    async finalizeCheckoutLowStockAndContinue(checkoutTrigger, form) {
        if (this.isCheckoutFinalizing) return;
        this.isCheckoutFinalizing = true;

        try {
            await this.updateTrackedLowStockItemsForCheckout();
        } catch (e) {
            // Never block checkout if enrichment fails
        }

        try {
            this.continueCheckoutNavigation(checkoutTrigger, form);
        } finally {
            setTimeout(() => {
                this.isCheckoutFinalizing = false;
            }, 500);
        }
    }

    continueCheckoutNavigation(checkoutTrigger, form) {
        if (!checkoutTrigger) {
            window.location.href = '/checkout';
            return;
        }

        const href = checkoutTrigger.getAttribute?.('href') || '';
        if (href && href.includes('/checkout')) {
            window.location.href = href;
            return;
        }

        const resolvedForm = form || checkoutTrigger.closest?.('form') || null;
        if (resolvedForm) {
            if (typeof resolvedForm.requestSubmit === 'function') {
                if (checkoutTrigger.tagName === 'BUTTON') {
                    resolvedForm.requestSubmit(checkoutTrigger);
                } else {
                    resolvedForm.requestSubmit();
                }
            } else {
                resolvedForm.submit();
            }
            return;
        }

        window.location.href = '/checkout';
    }

    async updateTrackedLowStockItemsForCheckout() {
        let cart = null;
        try {
            const response = await fetch('/cart.js', {
                headers: { Accept: 'application/json' }
            });
            if (!response.ok) return;
            cart = await response.json();
        } catch (e) {
            return;
        }

        if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

        const trackedItems = cart.items.filter(item => {
            const props = this.normalizeCartProperties(item.properties);
            return props._isLowStockOnAddToCart === '1';
        });

        for (const item of trackedItems) {
            await this.updateSingleTrackedLowStockItemForCheckout(item);
        }
    }

    normalizeCartProperties(properties) {
        const normalized = {};
        if (!properties || typeof properties !== 'object') return normalized;

        Object.entries(properties).forEach(([key, value]) => {
            if (value == null) return;
            const str = String(value);
            if (!str.trim()) return;
            normalized[key] = str;
        });
        return normalized;
    }

    toPositiveInt(value) {
        const parsed = parseInt(value, 10);
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        return parsed;
    }

    async updateSingleTrackedLowStockItemForCheckout(item) {
        if (!item || !item.key) return;

        const currentProps = this.normalizeCartProperties(item.properties);
        if (currentProps._isLowStockOnAddToCart !== '1') return;

        const handle = currentProps._lowStockHandle || item.handle || '';
        const variantId = currentProps._lowStockVariantId || String(item.variant_id || '');
        const leftOnAddToCart = this.toPositiveInt(currentProps._leftOnAddToCart);
        if (!handle || !variantId || leftOnAddToCart == null) return;

        const status = this.getScarcityStatus(handle, variantId);
        const leftOnCheckout = status && Number.isFinite(status.count) && status.count > 0
            ? status.count
            : leftOnAddToCart;

        const addedAtMs = this.toPositiveInt(currentProps._lowStockAddedAtMs);
        const minuteToCheckoutFromCart = addedAtMs == null
            ? null
            : Math.max(0, Math.round((Date.now() - addedAtMs) / 60000));

        // Remove deprecated properties from previous implementations.
        this.getDeprecatedLowStockPropertyNames().forEach(propName => {
            delete currentProps[propName];
        });

        currentProps._leftOnCheckout = String(leftOnCheckout);
        if (minuteToCheckoutFromCart != null) {
            currentProps._minuteToCheckoutFromCart = String(minuteToCheckoutFromCart);
        }

        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({
                    id: item.key,
                    quantity: item.quantity,
                    properties: currentProps
                })
            });

            this.queueTrackingEvent('checkout_low_stock_item_enriched', {
                handle,
                variantId,
                lineKey: item.key,
                leftOnAddToCart,
                leftOnCheckout,
                minuteToCheckoutFromCart
            });
        } catch (e) {
            // Keep checkout flow resilient
            this.queueTrackingEvent('checkout_low_stock_item_enrich_failed', {
                handle,
                variantId,
                lineKey: item.key,
                error: String(e?.message || e || 'unknown_error')
            });
        }
    }

    getFormHandle(form) {
        const picker = form.closest('variant-picker');
        if (picker) {
            return picker.getAttribute('handle');
        }

        const handleInput = form.querySelector('[name="handle"]');
        if (handleInput && handleInput.value) {
            return handleInput.value;
        }

        return window.location.pathname.split('/').pop();
    }

    getPageHandle() {
        const picker = document.querySelector('variant-picker');
        return picker?.getAttribute('handle') || window.location.pathname.split('/').pop();
    }

    getColorFromForm(form, handle) {
        const variantId = form.querySelector('input[name="id"]')?.value;

        // Try to find picker associated with this form
        let picker = form.closest('variant-picker');
        if (!picker && handle) {
            picker = document.querySelector(`variant-picker[handle="${handle}"]`);
        }



        const productData = picker ? this.parseProductData(picker) : null;

        let color = null;
        if (variantId && productData) {
            const variant = (productData.variants || []).find(v => String(v.id) === String(variantId));
            color = this.getVariantColor(variant);
        }

        // Fallback: Extract from option inputs if variant resolution failed
        if (!color) {
            // Check standard option inputs
            const colorInput = form.querySelector('input[name="option1"], select[name="option1"], input[name="Color"], select[name="Color"]');
            if (colorInput?.value) {
                color = colorInput.value;
            } else if (productData) {
                // Try matching options map
                const selectedOptions = {};
                form.querySelectorAll('input[name^="option"], select[name^="option"]').forEach(input => {
                    if (input.value) {
                        selectedOptions[input.name.toLowerCase()] = input.value;
                    }
                });

                const variant = this.findVariantByOptions(productData.variants, selectedOptions);
                color = this.getVariantColor(variant);
            }
        }

        return color || this.activeColors[handle];
    }

    parseProductData(picker) {
        try {
            const json = picker.querySelector('[data-product-variants-json]');
            return json ? JSON.parse(json.textContent) : null;
        } catch (e) {
            return null;
        }
    }

    findVariantByOptions(variants = [], selectedOptions = {}) {
        return variants.find(variant => {
            return Object.entries(selectedOptions).every(([name, value]) => {
                const optionValue = variant[name];
                return optionValue && optionValue.toLowerCase() === value.toLowerCase();
            });
        }) || null;
    }

    getVariantColor(variant) {
        if (!variant) return null;
        const options = this.getVariantOptionsArray(variant);
        if (!options.length) return null;

        for (const opt of options) {
            if (this.PRIORITY_COLORS.some(c => this.colorsMatch(c, opt))) return opt;
        }
        return options[0] || null;
    }

    getVariantOptionsArray(variant) {
        if (!variant) return [];
        if (Array.isArray(variant.options) && variant.options.length) {
            return variant.options;
        }

        return ['option1', 'option2', 'option3']
            .map(key => variant[key])
            .filter(Boolean);
    }

    /**
     * Create a variant key for lifecycle storage (handle_variantId)
     */
    getVariantKey(handle, variantId) {
        if (!handle || !variantId) return null;
        return `${handle}_${variantId}`;
    }

    /**
     * Format variant options as a human-readable string (e.g., "Green - Medium")
     */
    getVariantOptionsString(variant) {
        if (!variant) return '';
        const options = this.getVariantOptionsArray(variant);
        return options.filter(Boolean).join(' - ');
    }

    colorsMatch(c1, c2) {
        return (c1 || '').trim().toLowerCase() === (c2 || '').trim().toLowerCase();
    }

    /**
     * Check if a product handle is a bundle product
     * Uses bundles module from agb-corepack.min.js, with fallback to agb-bundles.js
     */
    isBundleProduct(handle) {
        if (!handle) return false;

        // Try to get bundles module from corepack first
        try {
            const Bundles = window.AGB?.require?.('bundles');
            if (Bundles && typeof Bundles.isBundle === 'function') {
                return Bundles.isBundle(handle);
            }
        } catch (e) {
            // Corepack bundles module not available, try fallback
        }

        // Fallback: Check if standalone bundles module exists (agb-bundles.js)
        // This is a safety net if corepack is not loaded yet
        try {
            if (window.AGB?.bundles?.isBundle && typeof window.AGB.bundles.isBundle === 'function') {
                return window.AGB.bundles.isBundle(handle);
            }
        } catch (e) {
            // Fallback also not available
        }

        return false;
    }

    /**
     * Core Logic: Record interaction with overwrite policy
     * "if something happens with a color that is the same or more important then the previos saved color then over write it"
     */
    recordInteraction(handle, color, type) {
        if (!this.state.interactions[handle]) {
            this.state.interactions[handle] = {};
        }

        const productInteractions = this.state.interactions[handle];
        const existing = productInteractions[color];
        const newWeight = this.PRIORITIES[type] || 0;

        if (!existing || newWeight >= existing.weight) {
            // Overwrite or create new record for this color
            productInteractions[color] = {
                type: type,
                timestamp: Date.now(),
                weight: newWeight
            };
            this.saveState('interaction_recorded', {
                handle,
                color,
                type,
                weight: newWeight
            });
            this.activeColors[handle] = color;
            this.maybeTrackFavoriteColor(handle, 'record_interaction');
            this.queueTrackingEvent('interaction_recorded', {
                handle,
                color,
                interactionType: type,
                weight: newWeight,
                favoriteColor: this.getBestColor(handle),
                interactionsForHandle: this.state.interactions[handle] || {}
            });
        }
    }

    /**
     * Core Logic: Calculate Best Color
     * 1. Backend (Recommended) - Only if LOCKED. (Wait, requirements say: "we cant change it untill at least 5 days later")
     * 2. Lifecycle Lock: If a color is currently in a scarcity lifecycle (active low stock), it MUST remain the favorite.
     * 3. Backend (Recommended) - If no lock.
     * 4. Calculated (Highest Weight)
     *    - If any high priority exists (> 66% logic -> if any active interaction, ignore 'load')
     * 5. Default
     */
    getBestColor(handle) {
        // 1. Lifecycle Lock Check
        // If a scarcity lifecycle is active (< 5 days), the locked color takes absolute priority
        // This ensures consistency - once we show low stock on a color, we can't change it
        const lifecycle = this.getLifecycleState(handle);
        if (lifecycle && lifecycle.activeColor && !this.isLifecycleExpired(lifecycle)) {
            return lifecycle.activeColor;
        }

        // 2. Calculate Local Priority First (from interactions)
        // Always calculate this as a fallback even when backend exists
        let calculatedColor = null;
        const productInteractions = this.state.interactions[handle];

        if (productInteractions) {
            const colors = Object.entries(productInteractions);

            if (colors.length > 0) {
                // Check if any "active" interaction exists (Click, Cart, Checkout)
                const hasActiveInteraction = colors.some(([_, data]) => data.weight > this.PRIORITIES['load']);

                let candidates = colors;
                if (hasActiveInteraction) {
                    candidates = colors.filter(([_, data]) => data.weight > this.PRIORITIES['load']);
                }

                // Find candidate with highest weight, tie-break with timestamp
                candidates.sort((a, b) => {
                    const weightDiff = b[1].weight - a[1].weight;
                    if (weightDiff !== 0) return weightDiff;
                    return b[1].timestamp - a[1].timestamp; // Newest wins tie
                });

                calculatedColor = candidates.length > 0 ? candidates[0][0] : null;
            }
        }

        // 3. Backend Priority (Product-specific recommended color)
        // Backend color overwrites locally calculated color (if valid)
        const backendColor = this.getRecommendedColor(handle);
        if (backendColor) return backendColor;

        // 4. Return calculated color from local interactions
        return calculatedColor;
    }

    /**
     * Get Backend Recommended Color for a specific product
     * Checks localStorage for recommendedProducts array and finds product by handle
     * Returns the camo (recommended color) for that product, or fallback if not found
     */
    getRecommendedColor(handle) {
        try {
            const recommendedProductsJson = localStorage.getItem('recommendedProducts');
            if (!recommendedProductsJson) return null;

            const recommendedProducts = JSON.parse(recommendedProductsJson);
            if (!Array.isArray(recommendedProducts) || recommendedProducts.length === 0) {
                return null;
            }

            // Find product-specific recommendation by handle (productLink)
            const productRecommendation = recommendedProducts.find(
                product => product.productLink === handle
            );

            // Return product-specific color if found
            if (productRecommendation && productRecommendation.camo) {
                return productRecommendation.camo;
            }

            // Fallback to first product's camo if no product-specific match
            if (recommendedProducts[0] && recommendedProducts[0].camo) {
                return recommendedProducts[0].camo;
            }

            return null;
        } catch (e) {
            // console.warn('ScarcityEngine: Error reading recommendedProducts', e);
            return null;
        }
    }

    getLifecycleState(handle, variantId = null) {
        if (!this.state.lifecycles) return null;

        // If variantId provided, use variant-specific key
        if (variantId) {
            const key = this.getVariantKey(handle, variantId);
            return this.state.lifecycles[key] || null;
        }

        // Fallback to old handle-only key for backward compatibility
        return this.state.lifecycles[handle] || null;
    }

    isLifecycleExpired(lifecycle) {
        // 24 Hour Lifecycle
        const LIFECYCLE_MS = 24 * 60 * 60 * 1000;
        return (Date.now() - lifecycle.startTime) > LIFECYCLE_MS;
    }

    /**
     * Count how many variant lifecycles currently have active (non-expired) scarcity lifecycles
     * Now counts variant-specific lifecycles (handle_variantId keys)
     */
    countActiveLifecycles() {
        if (!this.state.lifecycles) return 0;

        let count = 0;
        for (const key in this.state.lifecycles) {
            const lifecycle = this.state.lifecycles[key];
            if (lifecycle && (lifecycle.activeColor || lifecycle.variantId) && !this.isLifecycleExpired(lifecycle)) {
                count++;
            }
        }
        return count;
    }

    /**
     * Count how many active lifecycles exist for a specific product handle
     * Ensures we don't have more than 2 lifecycles per product
     */
    countActiveLifecyclesForProduct(handle) {
        if (!this.state.lifecycles || !handle) return 0;

        let count = 0;
        const handlePrefix = `${handle}_`;

        for (const key in this.state.lifecycles) {
            // Check if this lifecycle belongs to the product (key starts with handle_)
            if (key.startsWith(handlePrefix) || key === handle) {
                const lifecycle = this.state.lifecycles[key];
                if (lifecycle && (lifecycle.activeColor || lifecycle.variantId) && !this.isLifecycleExpired(lifecycle)) {
                    count++;
                }
            }
        }
        return count;
    }

    /**
     * Check if we can start a new scarcity lifecycle for a variant
     * Returns true if:
     * - The variant already has an active lifecycle (can continue existing one), OR
     * - The product has fewer than 2 active lifecycles AND we're under global limit
     */
    canStartLifecycle(handle, variantId = null) {
        // If this variant already has an active lifecycle, it can continue
        const existingLifecycle = this.getLifecycleState(handle, variantId);
        if (existingLifecycle && (existingLifecycle.activeColor || existingLifecycle.variantId) && !this.isLifecycleExpired(existingLifecycle)) {
            return true;
        }

        // Check per-product limit: max 2 lifecycles per product
        const productLifecycleCount = this.countActiveLifecyclesForProduct(handle);
        if (productLifecycleCount >= 2) {
            return false; // Product already has 2 lifecycles
        }

        // Check if we're at the global limit for new lifecycles
        const activeCount = this.countActiveLifecycles();
        return activeCount < this.MAX_ACTIVE_PRODUCTS;
    }

    /**
     * Get Global Scarcity Status for a Variant
     * Used by external components (e.g. Cart) to display consistent stock status
     * Now works with variant-specific keys (handle_variantId)
     */
    getScarcityStatus(handle, variantId = null) {
        // Check lifecycle first
        const lifecycle = this.getLifecycleState(handle, variantId);
        if (!lifecycle || this.isLifecycleExpired(lifecycle)) {
            return null;
        }

        // Backward compatibility: Generate and store random intervals for old lifecycles
        if (lifecycle.firstDropMs === undefined || lifecycle.secondDropMs === undefined) {
            lifecycle.firstDropMs = (Math.floor(Math.random() * 26) + 15) * 1000; // 15-40 seconds
            lifecycle.secondDropMs = (Math.floor(Math.random() * 26) + 15) * 60 * 1000; // 15-40 minutes
            this.saveState();
        }

        // Determine phase and current count based on new decay schedule
        const ageMs = Date.now() - lifecycle.startTime;
        let count = lifecycle.initialCount;
        let phase = 'initial';

        const firstDropMs = lifecycle.firstDropMs;
        const secondDropMs = lifecycle.secondDropMs;
        const oneHourMs = 60 * 60 * 1000; // 1 hour
        const twoHoursMs = 2 * 60 * 60 * 1000; // 2 hours

        // Apply drops in sequence:
        // T+15-40s: -1
        // T+15-40min: -1
        // T+1h: -1
        // T+2h: -1

        if (ageMs >= twoHoursMs) {
            count = Math.max(1, lifecycle.initialCount - 4);
            phase = 'decay_4';
        } else if (ageMs >= oneHourMs) {
            count = Math.max(1, lifecycle.initialCount - 3);
            phase = 'decay_3';
        } else if (ageMs >= secondDropMs) {
            count = Math.max(1, lifecycle.initialCount - 2);
            phase = 'decay_2';
        } else if (ageMs >= firstDropMs) {
            count = Math.max(1, lifecycle.initialCount - 1);
            phase = 'decay_1';
        }

        return {
            activeColor: lifecycle.activeColor, // Keep for backward compatibility
            variantId: lifecycle.variantId,
            variantOptions: lifecycle.variantOptions,
            count: count,
            phase: phase,
            startTime: lifecycle.startTime
        };
    }

    getStockData(handle, variantId) {
        if (!variantId) return { count: null, oos: false };

        // Check if variant is available by trying to find it in the page's product data
        // This ensures we don't show low stock for unavailable variants
        try {
            const picker = document.querySelector(`variant-picker[handle="${handle}"]`);
            if (picker) {
                const productData = this.parseProductData(picker);
                if (productData && productData.variants) {
                    const variant = productData.variants.find(v => String(v.id) === String(variantId));
                    // If variant exists but is not available or has no stock, return null
                    if (variant && (variant.available !== true || variant.inventory_quantity <= 0)) {
                        return { count: null, oos: false };
                    }
                }
            }
        } catch (e) {
            // Silently fail - continue with normal flow if we can't check availability
        }

        const key = `${handle}_${variantId}`;

        // Initialize or Get Lifecycle
        if (!this.state.lifecycles) this.state.lifecycles = {};

        // We rely on getScarcityStatus to be the single source of truth for count calculation
        // Now pass variantId to get variant-specific status
        const status = this.getScarcityStatus(handle, variantId);

        // If no active scarcity lifecycle for this variant, return normal state
        if (!status) {
            return { count: null, oos: false };
        }

        // Initialize product state if needed
        if (!this.state.products[key]) {
            this.state.products[key] = {
                initialCount: status.count, // Sync with lifecycle
                timestamp: Date.now()
            };
            this.saveState();
        }

        return { count: status.count, oos: false };
    }

    // Helper to lock the variant when we decide it's the one
    // This initializes the scarcity lifecycle for a specific variant
    // Only creates a new lifecycle if we're under the limit of MAX_ACTIVE_PRODUCTS
    lockLifecycleVariant(handle, variantId, variant = null) {
        if (!this.state.lifecycles) this.state.lifecycles = {};

        if (!variantId) return false;
        const key = this.getVariantKey(handle, variantId);

        // If lifecycle already exists for this variant, treat as success
        if (this.state.lifecycles[key]) {
            return true;
        }

        // Check if we can start a new lifecycle (limit check)
        if (!this.canStartLifecycle(handle, variantId)) {
            // At limit - cannot start new lifecycle
            this.queueTrackingEvent('lifecycle_start_blocked', {
                handle,
                variantId: String(variantId),
                activeLifecycleCount: this.countActiveLifecycles(),
                activeLifecycleCountForProduct: this.countActiveLifecyclesForProduct(handle),
                maxActiveProducts: this.MAX_ACTIVE_PRODUCTS
            });
            return false;
        }

        // Get variant options for display
        const variantOptions = variant ? this.getVariantOptionsString(variant) : null;
        const color = variant ? this.getVariantColor(variant) : null;

        // Determine initial stock count based on which product this is
        // First product: 8-9 items, Second product: 4-7 items
        const activeCount = this.countActiveLifecycles();
        const isFirstLifecycle = activeCount === 0;

        let initialCount;
        if (isFirstLifecycle) {
            // First product: 8-9 items
            initialCount = Math.floor(Math.random() * 2) + 8;
        } else {
            // Second product: 4-7 items
            initialCount = Math.floor(Math.random() * 4) + 4;
        }

        // Create new lifecycle with random intervals
        // Random intervals for first two drops: 15-40 seconds, 15-40 minutes
        const firstDropMs = (Math.floor(Math.random() * 26) + 15) * 1000; // 15-40 seconds
        const secondDropMs = (Math.floor(Math.random() * 26) + 15) * 60 * 1000; // 15-40 minutes

        this.state.lifecycles[key] = {
            startTime: Date.now(),
            variantId: variantId,
            variantOptions: variantOptions,
            activeColor: color, // Keep for backward compatibility
            initialCount: initialCount,
            firstDropMs: firstDropMs, // 15-40 seconds
            secondDropMs: secondDropMs // 15-40 minutes
        };
        this.saveState('lifecycle_started', {
            handle,
            variantId: String(variantId),
            color: color || null,
            variantOptions: variantOptions || null,
            initialCount,
            firstDropMs,
            secondDropMs,
            isFirstLifecycle
        });
        this.queueTrackingEvent('lifecycle_started', {
            handle,
            variantId: String(variantId),
            color: color || null,
            variantOptions: variantOptions || null,
            initialCount,
            firstDropMs,
            secondDropMs,
            isFirstLifecycle
        });
        this.maybeTrackFavoriteColor(handle, 'lifecycle_started');

        // Note: Patch promotion is triggered via checkAndTriggerPatchPromotionOnCartAdd() after cart add
        // (Removed premature trigger that was firing on lifecycle creation)

        return true;
    }

    // Legacy method name for backward compatibility
    lockLifecycleColor(handle, color) {
        // For backward compatibility, try to find a variant with this color
        // But prefer using lockLifecycleVariant directly
        return false; // Deprecated - use lockLifecycleVariant instead
    }

    /**
     * Trigger free patch promotion when first lifecycle starts
     */
    async triggerFreePatchPromotion() {
        // TEMPORARILY DISABLED
        if (!this.PATCH_PROMOTION_ENABLED) {
            return;
        }

        // Prevent concurrent patch additions (race condition protection)
        if (this.isAddingPatch) {
            return;
        }

        // Check if promotion already active or was already used
        if (this.state.freePatchPromotion && this.state.freePatchPromotion.isActive) {
            return; // Already active, don't add again
        }

        // Check 2-week cooldown
        if (this.state.freePatchPromotion && this.state.freePatchPromotion.cooldownUntil) {
            const now = Date.now();
            if (now < this.state.freePatchPromotion.cooldownUntil) {
                return;
            } else {
                // Cooldown expired, clear old state
                this.state.freePatchPromotion = null;
                this.saveState();
            }
        }

        // Note: We don't check addedAt here because cooldown check above already handles expired promotions
        // If cooldown expired, state is cleared, so we can proceed

        // Check if patch is already in cart
        const cart = await this.getCartData();
        if (cart && cart.items) {
            const patchInCart = cart.items.find(item =>
                item.product_handle === '4th-of-july-us-scorpion-patch'
            );
            if (patchInCart) {
                // Patch already in cart, just activate promotion tracking
                this.state.freePatchPromotion = {
                    addedAt: Date.now(),
                    patchLineKey: patchInCart.key,
                    isActive: true,
                    cooldownUntil: Date.now() + this.PATCH_COOLDOWN_MS // 2 weeks from now
                };
                this.saveState();
                this.startPatchTimer();
                return;
            }
        }

        // Set flag to prevent concurrent additions
        this.isAddingPatch = true;

        // Add patch to cart
        await this.addFreePatchToCart();
    }

    /**
     * Get current cart data
     */
    async getCartData() {
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            return null;
        }
    }

    /**
     * Add free patch to cart
     */
    async addFreePatchToCart() {
        const PATCH_HANDLE = '4th-of-july-us-scorpion-patch';

        try {
            // Fetch patch product data
            const productResponse = await fetch(`/products/${PATCH_HANDLE}.js`);

            if (!productResponse.ok) {
                this.isAddingPatch = false; // Reset flag
                return;
            }

            const productData = await productResponse.json();

            if (!productData || !productData.variants || productData.variants.length === 0) {
                this.isAddingPatch = false; // Reset flag
                return;
            }

            // Get first available variant
            const variant = productData.variants.find(v => v.available) || productData.variants[0];

            if (!variant || !variant.id) {
                this.isAddingPatch = false; // Reset flag
                return;
            }

            // Add to cart
            const addResponse = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    items: [{
                        id: variant.id,
                        quantity: 1
                    }]
                })
            });

            if (!addResponse.ok) {
                this.isAddingPatch = false; // Reset flag
                return;
            }

            await addResponse.json();

            // Wait a moment for cart to update
            await new Promise(resolve => setTimeout(resolve, 200));

            // Get updated cart to find the patch line key
            const cart = await this.getCartData();

            if (cart && cart.items) {
                const patchItem = cart.items.find(item =>
                    item.product_handle === PATCH_HANDLE
                );

                if (patchItem) {
                    // Store promotion state
                    this.state.freePatchPromotion = {
                        addedAt: Date.now(),
                        patchLineKey: patchItem.key,
                        isActive: true,
                        cooldownUntil: Date.now() + this.PATCH_COOLDOWN_MS // 2 weeks from now
                    };
                    this.saveState();

                    // Start timer
                    this.startPatchTimer();

                    // Trigger cart refresh events
                    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                        bubbles: true,
                        detail: {
                            baseEvent: 'patch:added',
                            cart: cart
                        }
                    }));

                    // Refresh cart UI
                    setTimeout(() => {
                        this.refreshCartItems();
                    }, 100);

                    // Reset flag after successful addition
                    this.isAddingPatch = false;
                } else {
                    this.isAddingPatch = false; // Reset flag
                }
            } else {
                this.isAddingPatch = false; // Reset flag
            }
        } catch (e) {
            this.isAddingPatch = false; // Reset flag
        }
    }

    /**
     * Initialize patch promotion on page load
     */
    initPatchPromotion() {
        // TEMPORARILY DISABLED
        if (!this.PATCH_PROMOTION_ENABLED) {
            return;
        }

        if (!this.state.freePatchPromotion || !this.state.freePatchPromotion.isActive) {
            return;
        }

        // Check if promotion is still valid (within 2 hours)
        const addedAt = this.state.freePatchPromotion.addedAt;
        const twoHoursMs = 2 * 60 * 60 * 1000;
        const elapsed = Date.now() - addedAt;

        if (elapsed >= twoHoursMs) {
            // Promotion expired, remove patch
            this.removeFreePatchFromCart();
        } else {
            // Promotion still active, start timer
            this.startPatchTimer();
        }
    }

    /**
     * Start patch timer interval
     */
    startPatchTimer() {
        // Clear any existing timer
        if (this.patchTimerInterval) {
            clearInterval(this.patchTimerInterval);
        }

        // Update timer immediately
        this.updatePatchTimer();

        // Update every second
        this.patchTimerInterval = setInterval(() => {
            this.updatePatchTimer();
        }, 1000);
    }

    /**
     * Update patch timer display and check for expiration
     */
    async updatePatchTimer() {
        if (!this.state.freePatchPromotion || !this.state.freePatchPromotion.isActive) {
            if (this.patchTimerInterval) {
                clearInterval(this.patchTimerInterval);
                this.patchTimerInterval = null;
            }
            return;
        }

        const addedAt = this.state.freePatchPromotion.addedAt;
        const twoHoursMs = 2 * 60 * 60 * 1000;
        const elapsed = Date.now() - addedAt;
        const remaining = twoHoursMs - elapsed;

        if (remaining <= 0) {
            // Timer expired, remove patch
            this.removeFreePatchFromCart();
            return;
        }

        // Calculate hours, minutes, seconds
        const hours = Math.floor(remaining / (60 * 60 * 1000));
        const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
        const seconds = Math.floor((remaining % (60 * 1000)) / 1000);

        // Format timer string
        let timerText = '';
        if (hours > 0) {
            timerText = `${hours}h ${minutes}m ${seconds}s remaining`;
        } else if (minutes > 0) {
            timerText = `${minutes}m ${seconds}s remaining`;
        } else {
            timerText = `${seconds}s remaining`;
        }

        // Render timer
        this.renderPatchTimer(timerText);
    }

    /**
     * Render patch timer in both banner and inline locations
     */
    renderPatchTimer(timerText) {
        // Update banner timer (at top of line-item)
        const bannerTimers = document.querySelectorAll('.free-patch-timer-banner');
        bannerTimers.forEach((banner) => {
            banner.textContent = timerText;
            banner.style.display = ''; // Show element
            banner.style.visibility = 'visible'; // Ensure visible
        });

        // Update inline timer (in line-item)
        const inlineTimers = document.querySelectorAll('.free-patch-timer-inline');
        inlineTimers.forEach((timer) => {
            timer.textContent = timerText;
            timer.style.display = ''; // Show element
            timer.style.visibility = 'visible'; // Ensure visible
        });
    }

    /**
     * Check patch promotion status and render/remove timer
     */
    async checkPatchPromotion() {
        // TEMPORARILY DISABLED
        if (!this.PATCH_PROMOTION_ENABLED) {
            // Hide timer displays if promotion is disabled
            document.querySelectorAll('.free-patch-timer-banner, .free-patch-timer-inline').forEach(el => {
                el.style.display = 'none';
            });
            return;
        }

        if (!this.state.freePatchPromotion || !this.state.freePatchPromotion.isActive) {
            // Hide timer displays if promotion not active
            document.querySelectorAll('.free-patch-timer-banner, .free-patch-timer-inline').forEach(el => {
                el.style.display = 'none';
            });
            return;
        }

        // Get current cart to verify patch is still there
        const cart = await this.getCartData();
        if (!cart || !cart.items || cart.items.length === 0) {
            // Cart cleared or unavailable, clear promotion but preserve cooldown
            const cooldownUntil = this.state.freePatchPromotion?.cooldownUntil || (Date.now() + this.PATCH_COOLDOWN_MS);
            this.state.freePatchPromotion = {
                addedAt: null,
                patchLineKey: null,
                isActive: false,
                cooldownUntil: cooldownUntil
            };
            this.saveState();
            if (this.patchTimerInterval) {
                clearInterval(this.patchTimerInterval);
                this.patchTimerInterval = null;
            }
            document.querySelectorAll('.free-patch-timer-banner, .free-patch-timer-inline').forEach(el => {
                el.style.display = 'none';
            });
            return;
        }

        const PATCH_HANDLE = '4th-of-july-us-scorpion-patch';
        const patchItem = cart.items.find(item => item.product_handle === PATCH_HANDLE);

        if (!patchItem) {
            // Patch was manually removed or cart cleared, clear promotion but preserve cooldown
            const cooldownUntil = this.state.freePatchPromotion?.cooldownUntil || (Date.now() + this.PATCH_COOLDOWN_MS);
            this.state.freePatchPromotion = {
                addedAt: null,
                patchLineKey: null,
                isActive: false,
                cooldownUntil: cooldownUntil
            };
            this.saveState();
            if (this.patchTimerInterval) {
                clearInterval(this.patchTimerInterval);
                this.patchTimerInterval = null;
            }
            document.querySelectorAll('.free-patch-timer-banner, .free-patch-timer-inline').forEach(el => {
                el.style.display = 'none';
            });
            return;
        }

        // Update patch line key if it changed (shouldn't happen, but handle it)
        if (patchItem.key !== this.state.freePatchPromotion.patchLineKey) {
            this.state.freePatchPromotion.patchLineKey = patchItem.key;
            this.saveState();
        }

        // Check if promotion expired
        const addedAt = this.state.freePatchPromotion.addedAt;
        const twoHoursMs = 2 * 60 * 60 * 1000;
        const elapsed = Date.now() - addedAt;

        if (elapsed >= twoHoursMs) {
            // Expired, remove patch
            this.removeFreePatchFromCart();
            return;
        }

        // Promotion active, ensure timer is running
        if (!this.patchTimerInterval) {
            this.startPatchTimer();
        }

        // Render timer on patch line item
        this.renderPatchTimerOnLineItem(patchItem.key);

        // Force immediate update to show timer text
        this.updatePatchTimer();
    }

    /**
     * Render timer on specific line item
     */
    renderPatchTimerOnLineItem(lineKey) {
        // Find line-item element for this patch
        const selector = `line-item input[data-line-key="${lineKey}"]`;
        const inputElement = document.querySelector(selector);

        const lineItem = inputElement?.closest('line-item');

        if (!lineItem) {
            return;
        }

        // Update or create banner timer
        let bannerTimer = lineItem.querySelector('.free-patch-timer-banner');

        if (!bannerTimer) {
            bannerTimer = document.createElement('div');
            bannerTimer.className = 'free-patch-timer-banner';
            bannerTimer.style.cssText = 'color: #D32F2F; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.025em; padding: 4px 0;';
            // Insert at top of line-item-info
            const lineItemInfo = lineItem.querySelector('.line-item-info');
            if (lineItemInfo) {
                lineItemInfo.insertBefore(bannerTimer, lineItemInfo.firstChild);
            }
        }

        // IMPORTANT: Show the banner immediately
        bannerTimer.style.display = '';
        bannerTimer.style.visibility = 'visible';

        // Update or create inline timer
        let inlineTimer = lineItem.querySelector('.free-patch-timer-inline');

        if (!inlineTimer) {
            inlineTimer = document.createElement('span');
            inlineTimer.className = 'free-patch-timer-inline';
            inlineTimer.style.cssText = 'color: #D32F2F; font-weight: 600; font-size: 11px; margin-left: 8px;';
            // Insert after product title or price
            const title = lineItem.querySelector('.line-item-info a, .line-item-info p');
            if (title) {
                title.insertAdjacentElement('afterend', inlineTimer);
            }
        }

        // IMPORTANT: Show the inline timer immediately
        inlineTimer.style.display = '';
        inlineTimer.style.visibility = 'visible';

        // Timer text will be updated by updatePatchTimer() method
    }

    /**
     * Remove free patch from cart when timer expires
     */
    async removeFreePatchFromCart() {
        // Clear timer interval
        if (this.patchTimerInterval) {
            clearInterval(this.patchTimerInterval);
            this.patchTimerInterval = null;
        }

        const promotion = this.state.freePatchPromotion;
        if (!promotion || !promotion.patchLineKey) {
            // Clear state but preserve cooldown
            const cooldownUntil = promotion?.cooldownUntil || (Date.now() + this.PATCH_COOLDOWN_MS);
            this.state.freePatchPromotion = {
                addedAt: null,
                patchLineKey: null,
                isActive: false,
                cooldownUntil: cooldownUntil
            };
            this.saveState();
            return;
        }

        try {
            // Remove patch from cart
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    id: promotion.patchLineKey,
                    quantity: 0
                })
            });

            // Clear promotion state but preserve cooldown
            const cooldownUntil = promotion.cooldownUntil || (Date.now() + this.PATCH_COOLDOWN_MS);
            this.state.freePatchPromotion = {
                addedAt: null,
                patchLineKey: null,
                isActive: false,
                cooldownUntil: cooldownUntil
            };
            this.saveState();

            // Hide timer displays
            document.querySelectorAll('.free-patch-timer-banner, .free-patch-timer-inline').forEach(el => {
                el.style.display = 'none';
            });

            // Refresh cart if on cart page
            if (window.location.pathname.includes('/cart')) {
                window.location.reload();
            } else {
                // Trigger cart refresh event
                document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: { baseEvent: 'patch:removed' }
                }));
            }
        } catch (e) {
            // Clear state but preserve cooldown even if removal fails
            const cooldownUntil = promotion?.cooldownUntil || (Date.now() + this.PATCH_COOLDOWN_MS);
            this.state.freePatchPromotion = {
                addedAt: null,
                patchLineKey: null,
                isActive: false,
                cooldownUntil: cooldownUntil
            };
            this.saveState();
        }
    }

    refreshUI(handle) {
        // 1. Refresh PDP (if present)
        if (handle || document.querySelector('variant-picker')) {
            this.refreshPDP(handle);
        }

        // 2. Refresh Cart Items (always)
        this.refreshCartItems();
    }

    refreshCartItems() {
        // Check and handle free patch promotion
        this.checkPatchPromotion();

        document.querySelectorAll('.cart-low-stock-variant').forEach((elem) => {
            const handle = elem.dataset.productHandle;
            const variantColor = elem.dataset.variantColor;
            const variantId = elem.dataset.variantId;
            const realStock = parseInt(elem.dataset.realStock, 10);
            const lineKey = elem.dataset.lineKey;

            if (!handle || !lineKey) {
                elem.style.display = 'none';
                return;
            }

            // 1. Start with the default limit (69)
            let finalLimit = 69;

            // 2. Apply Real Stock Limit (if valid and lower)
            let hasRealLowStock = false;
            if (!isNaN(realStock)) {
                // If real stock is available, it's a hard limit
                finalLimit = Math.min(finalLimit, Math.max(0, realStock));

                // Check if we should show the "Only X left" message for real stock
                // Only show if variant is available and has stock
                if (this.realStockEnabled && realStock <= this.REAL_STOCK_THRESHOLD && realStock > 0) {
                    // Verify variant is available before showing (check via product data if available)
                    let isVariantAvailable = true;
                    if (handle && variantId) {
                        try {
                            const picker = document.querySelector(`variant-picker[handle="${handle}"]`);
                            if (picker) {
                                const productData = this.parseProductData(picker);
                                if (productData && productData.variants) {
                                    const variant = productData.variants.find(v => String(v.id) === String(variantId));
                                    if (variant && (variant.available !== true || variant.inventory_quantity <= 0)) {
                                        isVariantAvailable = false;
                                    }
                                }
                            }
                        } catch (e) {
                            // Silently fail - assume available if we can't check
                        }
                    }

                    if (isVariantAvailable) {
                        elem.innerHTML = `<p class="text-red-600 text-xs uppercase tracking-wide font-semibold" style="color: #D32F2F;">only ${realStock} left in stock</p>`;
                        elem.style.display = '';
                        hasRealLowStock = true;
                    }
                }
            }

            // 3. Apply Fake Scarcity Limit (if applicable and lower)
            // Use variant-specific lifecycle if variantId is available
            if (!hasRealLowStock && this.uiEnabled && variantId) {
                // Verify variant is available before showing fake scarcity
                let isVariantAvailable = true;
                if (handle && variantId) {
                    try {
                        const picker = document.querySelector(`variant-picker[handle="${handle}"]`);
                        if (picker) {
                            const productData = this.parseProductData(picker);
                            if (productData && productData.variants) {
                                const variant = productData.variants.find(v => String(v.id) === String(variantId));
                                if (variant && (variant.available !== true || variant.inventory_quantity <= 0)) {
                                    isVariantAvailable = false;
                                }
                            }
                        }
                    } catch (e) {
                        // Silently fail - assume available if we can't check
                    }
                }

                if (isVariantAvailable) {
                    const status = this.getScarcityStatus(handle, variantId);

                    if (status && status.count !== null && status.count > 0) {
                        // This variant has fake scarcity
                        const variantOptions = status.variantOptions || variantColor || '';
                        elem.innerHTML = `<p class="text-red-600 text-xs uppercase tracking-wide font-semibold" style="color: #D32F2F;">only ${status.count} left in stock</p>`;
                        elem.style.display = '';

                        // Apply fake limit
                        finalLimit = Math.min(finalLimit, status.count);
                    } else if (variantColor) {
                        // Fallback to old color-based check for backward compatibility
                        const status = this.getScarcityStatus(handle);
                        if (status && status.activeColor && this.colorsMatch(variantColor, status.activeColor)) {
                            elem.innerHTML = `<p class="text-red-600 text-xs uppercase tracking-wide font-semibold" style="color: #D32F2F;">only ${status.count} left in stock</p>`;
                            elem.style.display = '';
                            finalLimit = Math.min(finalLimit, status.count);
                        } else {
                            elem.style.display = 'none';
                        }
                    } else {
                        elem.style.display = 'none';
                    }
                } else {
                    elem.style.display = 'none';
                }
            } else if (!hasRealLowStock) {
                elem.style.display = 'none';
            }

            // 4. Apply the final calculated limit to the input and UI
            elem.dataset.limit = finalLimit;
            this.updateLineQuantityLimit(lineKey, finalLimit);
        });

        // Also re-attach listeners to any new inputs that may have been added
        this.attachQuantityInputListeners();
    }

    refreshPDP(handle) {
        // console.log('refreshPDP called for handle:', handle);
        if (!handle) {
            const picker = document.querySelector('variant-picker');
            handle = picker?.getAttribute('handle');
        }
        if (!handle) return;

        const picker = document.querySelector(`variant-picker[handle="${handle}"]`);
        if (!picker) {
            // console.log('No variant picker found for handle:', handle);
            return;
        }

        // Bundle sync: Check if this is a bundle product
        if (this.isBundleProduct(handle)) {
            // Fire async bundle refresh (non-blocking)
            this.refreshBundlePDP(handle, picker).catch(() => {
                // Silently handle errors
            });
            return;
        }

        const selectedVariantId = this.getSelectedVariantId(picker);
        const selectedColor = this.getSelectedColor(picker);
        // console.log('Selected Variant ID:', selectedVariantId);

        // Determine which low stock system applies to the currently selected variant
        // REAL STOCK: Independent check - if selected variant has real inventory ≤ threshold
        // FAKE SCARCITY: Only applies to the "favorite color" variant

        let showRealStock = false;
        let realStockCount = null;

        // Check real stock for selected variant (completely independent, no slots, no storage)
        if (this.realStockEnabled && selectedVariantId) {
            // Get variant object to check availability
            let variant = null;
            try {
                const productData = this.parseProductData(picker);
                if (productData && productData.variants) {
                    variant = productData.variants.find(v => String(v.id) === String(selectedVariantId));
                }
            } catch (e) {
                // Silently fail
            }

            // Only check stock if variant exists and is available
            if (variant && variant.available === true && variant.inventory_quantity > 0) {
                const realStock = this.getRealStockForVariant(picker, selectedVariantId);
                // console.log('Real Stock Check:', { realStock, threshold: this.REAL_STOCK_THRESHOLD });

                if (realStock !== null && realStock <= this.REAL_STOCK_THRESHOLD && realStock > 0) {
                    showRealStock = true;
                    realStockCount = realStock;
                }
            }
        }

        // If selected variant has real low stock, show real stock UI
        if (showRealStock) {
            // console.log('Showing Real Low Stock UI:', realStockCount);
            this.showRealLowStock(picker, selectedVariantId, realStockCount);
            return;
        }

        // Clear any previous low stock UI before checking fake scarcity
        this.clearAllLowStockUI(picker);
        
        // Note: We don't fix swatch availability states here - the Liquid template handles this correctly
        // Our fixSwatchAvailabilityStates() method was causing conflicts with the template's logic

        // Add artificial swatches next to favorite color (if enabled AND exists AND product has active lifecycle)
        if (this.artificialSwatchesEnabled) {
            const favoriteColor = this.getBestColor(handle);
            
            // Check if this product has an active low stock lifecycle
            const productHasActiveLifecycle = this.countActiveLifecyclesForProduct(handle) > 0;
            
            // Check if swatch lifecycle is valid (2-week lifecycle)
            const hasValidSwatchLifecycle = this.isSwatchLifecycleValid(handle);
            
            if (favoriteColor && productHasActiveLifecycle) {
                // If swatch lifecycle is valid or doesn't exist yet, add swatches
                // (addArtificialSwatches will create lifecycle if needed)
                if (hasValidSwatchLifecycle || !this.state.artificialSwatches || !this.state.artificialSwatches[handle]) {
                    this.addArtificialSwatches(picker, favoriteColor);
                } else {
                    // Lifecycle expired - remove swatches and clean up
                    this.removeArtificialSwatches(picker);
                    delete this.state.artificialSwatches[handle];
                    this.saveState();
                }
            } else {
                // Remove artificial swatches if no favorite color or no active lifecycle
                this.removeArtificialSwatches(picker);
            }
        } else {
            // Artificial swatches disabled - remove any existing ones
            this.removeArtificialSwatches(picker);
        }

        // FAKE SCARCITY SYSTEM (independent of real stock, uses slots/storage)
        if (!this.uiEnabled) {
            // console.log('Fake scarcity UI disabled');
            return;
        }

        // Track by complete variant instead of just color
        if (!selectedVariantId) return;

        // Get the variant object for tracking
        let variant = null;
        try {
            const productData = this.parseProductData(picker);
            if (productData && productData.variants) {
                variant = productData.variants.find(v => String(v.id) === String(selectedVariantId));
            }
        } catch (e) {
            // Silently fail
        }

        if (!variant) return;

        // Check if variant is available and has stock before showing fake scarcity
        if (variant.available !== true || variant.inventory_quantity <= 0) {
            return;
        }

        // Check if this specific variant already has an active lifecycle
        const existingLifecycle = this.getLifecycleState(handle, selectedVariantId);
        const hasActiveLifecycle = existingLifecycle && !this.isLifecycleExpired(existingLifecycle);

        // LOCK: If this variant doesn't have an active lifecycle, try to lock it now.
        if (!hasActiveLifecycle) {
            const lifecycleCreated = this.lockLifecycleVariant(handle, selectedVariantId, variant);
            if (!lifecycleCreated) {
                // At slot limit - can't show fake scarcity
                return;
            }
        }

        // Get stock data for this specific variant
        const stock = this.getStockData(handle, selectedVariantId);
        if (stock.count === null) {
            return;
        }

        // Show fake scarcity UI for this variant
        const color = this.getVariantColor(variant);
        if (color) {
            this.showFakeScarcityUI(picker, color, stock.count, selectedVariantId, handle);
        }
    }

    /**
     * Refresh bundle product page with low stock display for bundle components
     * Uses BUNDLE_DATA from bundles module to resolve all components and check their low stock
     */
    async refreshBundlePDP(handle, picker) {
        try {
            // Validate inputs
            if (!handle || !picker) {
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
                return;
            }

            // Get selected variant ID
            const selectedVariantId = this.getSelectedVariantId(picker);
            if (!selectedVariantId) {
                // No variant selected, clear UI
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
                return;
            }

            // Verify bundles module is available
            const Bundles = this.getBundlesModule();
            if (!Bundles) {
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
                return;
            }

            // Verify this is actually a bundle
            if (!Bundles.isBundle || !Bundles.isBundle(handle)) {
                // Not a bundle, should not have been called
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
                return;
            }

            // Resolve bundle components using BUNDLE_DATA
            const components = await this.resolveBundleComponents(handle, selectedVariantId);
            if (!components || !Array.isArray(components) || components.length === 0) {
                // No components resolved, clear UI
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
                return;
            }

            // Get baseItem (first component, option1 - e.g., K-Zero)
            const baseItem = components[0];
            if (!baseItem) {
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
                return;
            }

            // Get selected option1 color from bundle variant (baseItem color)
            // The bundle variant has options array where option1 is the baseItem color
            let selectedBaseItemColor = null;
            try {
                const bundleData = this.getBundleVariant(picker);
                if (bundleData && bundleData.variant && Array.isArray(bundleData.variant.options)) {
                    selectedBaseItemColor = bundleData.variant.options[0]; // option1 is baseItem color
                }
            } catch (e) {
            }

            // Fallback to baseItem.color from resolved component
            if (!selectedBaseItemColor && baseItem.color) {
                selectedBaseItemColor = baseItem.color;
            }

            if (!selectedBaseItemColor) {
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
                return;
            }

            // Get low stock status for baseItem (syncs with individual product page)
            const baseItemStock = await this.getBaseItemLowStock(baseItem);

            if (baseItemStock && baseItemStock.count > 0) {
                // Display baseItem low stock on bundle page, synced with individual product
                const variantOptions = baseItemStock.variantOptions || baseItem.color || '';

                // Update banner with variant-specific info
                this.updateLowStockBanner(baseItemStock.count, variantOptions);

                // Find and update the option1 swatch wrapper for the selected color
                const swatches = picker.querySelectorAll('.variant-picker__swatch-wrapper');
                for (const wrapper of swatches) {
                    // Only process option1 swatches (color swatches)
                    const input = wrapper.querySelector('input');
                    if (!input || input.name !== 'option1') continue;

                    const wrapperColor = wrapper.dataset.colorValue || wrapper.dataset.swatchValue;
                    const isSelected = this.colorsMatch(wrapperColor, selectedBaseItemColor) ||
                        wrapper.querySelector('input:checked') ||
                        wrapper.classList.contains('selected') ||
                        wrapper.classList.contains('is-selected');

                    if (isSelected && this.colorsMatch(wrapperColor, selectedBaseItemColor)) {
                        // This is the selected baseItem color - show low stock UI
                        wrapper.classList.add('low-stock-active', 'has-favorite-color');

                        // Show flag
                        const flag = wrapper.querySelector('.variant-picker__few-items-flag');
                        if (flag) {
                            flag.textContent = `only ${baseItemStock.count} left`;
                            flag.style.display = 'block';
                        }

                        // Add border to swatch
                        const swatch = wrapper.querySelector('.color-swatch') || wrapper;
                        swatch.classList.add('low-stock-border');
                    } else {
                        // Clear low stock UI for other swatches
                        wrapper.classList.remove('low-stock-active', 'has-favorite-color');
                        const swatch = wrapper.querySelector('.color-swatch') || wrapper;
                        swatch.classList.remove('low-stock-border');
                        const flag = wrapper.querySelector('.variant-picker__few-items-flag');
                        if (flag) flag.style.display = 'none';
                    }
                }
            } else {
                // No low stock for baseItem, clear UI
                this.clearAllLowStockUI(picker);
                this.clearLowStockBanner();
            }
        } catch (e) {
            // On error, clear UI to avoid showing stale data
            this.clearAllLowStockUI(picker);
            this.clearLowStockBanner();
        }
    }

    /**
     * Get the currently selected color from the picker
     */
    getSelectedColor(picker) {
        // Check for checked input in swatch wrapper
        const checkedWrapper = picker?.querySelector('.variant-picker__swatch-wrapper:has(input:checked)');
        if (checkedWrapper?.dataset.colorValue) return checkedWrapper.dataset.colorValue;

        // Check for selected class
        const selectedWrapper = picker?.querySelector('.variant-picker__swatch-wrapper.is-selected, .variant-picker__swatch-wrapper.selected');
        if (selectedWrapper?.dataset.colorValue) return selectedWrapper.dataset.colorValue;

        // Try to get from variant data
        const selectedVariantId = this.getSelectedVariantId(picker);
        if (selectedVariantId) {
            try {
                const json = picker.querySelector('[data-product-variants-json]');
                const data = JSON.parse(json.textContent);
                const variant = data.variants.find(v => String(v.id) === String(selectedVariantId));
                return this.getVariantColor(variant);
            } catch (e) { }
        }

        return null;
    }

    /**
     * Get swatch wrapper element for a specific color
     * IMPORTANT: Only returns real swatches, never artificial ones
     */
    /**
     * Get swatch wrapper for a color (template-agnostic)
     * Works with both standard variant-picker.liquid (with wrappers)
     * and variant-picker-agb-system.liquid (wrappers commented out)
     * @param {HTMLElement} picker - The variant picker element
     * @param {string} color - The color name to find
     * @returns {HTMLElement|null} - The wrapper element or null if not found
     */
    getSwatchWrapperForColor(picker, color) {
        if (!picker || !color) return null;

        // APPROACH 1: Try standard structure first (with wrappers)
        const swatches = picker.querySelectorAll('.variant-picker__swatch-wrapper');
        for (const wrapper of swatches) {
            // Skip artificial swatches - only find real swatches
            if (wrapper.getAttribute('data-artificial') === 'true') {
                continue;
            }

            // Ensure it's option1
            const input = wrapper.querySelector('input');
            if (input && input.name !== 'option1') continue;

            // Check data-color-value attribute
            if (this.colorsMatch(wrapper.dataset.colorValue, color)) {
                return wrapper;
            }
        }

        // APPROACH 2: Fallback for AGB structure (no wrappers, direct labels)
        // Look for color-swatch labels with data-option-value
        const labels = picker.querySelectorAll('.color-swatch[data-option-value]');
        for (const label of labels) {
            // Get color from SR-only span or label content
            const srSpan = label.querySelector('.sr-only');
            const colorText = srSpan ? srSpan.textContent.trim() : label.textContent.trim();

            if (this.colorsMatch(colorText, color)) {
                // Check if label is already wrapped (might have been wrapped earlier)
                const existingWrapper = label.closest('.variant-picker__swatch-wrapper');
                if (existingWrapper && existingWrapper.getAttribute('data-artificial') !== 'true') {
                    return existingWrapper;
                }

                // Create wrapper on-the-fly for AGB template
                const wrapper = document.createElement('div');
                wrapper.className = 'variant-picker__swatch-wrapper';
                wrapper.setAttribute('data-color-value', color);

                // Get associated input (should be sibling or nearby)
                const input = label.previousElementSibling;
                if (input && input.tagName === 'INPUT' && input.name === 'option1') {
                    // Wrap both input and label
                    const parent = label.parentNode;
                    parent.insertBefore(wrapper, input);
                    wrapper.appendChild(input);
                    wrapper.appendChild(label);
                } else {
                    // Just wrap the label
                    label.parentNode.insertBefore(wrapper, label);
                    wrapper.appendChild(label);
                }

                return wrapper;
            }
        }

        return null;
    }

    /**
     * Get all available colors from the product's variants
     * Returns an array of unique color names (option1) that exist in the product
     */
    getAvailableColorsFromProduct(picker) {
        if (!picker) return [];
        
        try {
            const productData = this.parseProductData(picker);
            if (!productData || !productData.variants) return [];
            
            const colors = new Set();
            productData.variants.forEach(variant => {
                const color = this.getVariantColor(variant);
                if (color) {
                    colors.add(color);
                }
            });
            
            return Array.from(colors);
        } catch (e) {
            return [];
        }
    }

    /**
     * Check if swatch lifecycle is still valid (not expired)
     */
    isSwatchLifecycleValid(handle) {
        if (!handle || !this.state.artificialSwatches) return false;
        
        const swatchLifecycle = this.state.artificialSwatches[handle];
        if (!swatchLifecycle) return false;
        
        // Check if lifecycle has expired
        const now = Date.now();
        if (swatchLifecycle.expiresAt && now >= swatchLifecycle.expiresAt) {
            return false; // Expired
        }
        
        return true; // Valid
    }

    /**
     * Clean up expired swatch lifecycles from state
     */
    checkAndCleanupSwatchLifecycles() {
        if (!this.state.artificialSwatches) return;
        
        const now = Date.now();
        let hasChanges = false;
        
        for (const handle in this.state.artificialSwatches) {
            const swatchLifecycle = this.state.artificialSwatches[handle];
            if (swatchLifecycle && swatchLifecycle.expiresAt && now >= swatchLifecycle.expiresAt) {
                // Lifecycle has expired, remove it
                delete this.state.artificialSwatches[handle];
                hasChanges = true;
            }
        }
        
        // Save state if any changes were made
        if (hasChanges) {
            this.saveState();
        }
    }

    /**
     * Remove artificial swatches (Wolf Grey and Coyote Tan) from the picker
     */
    removeArtificialSwatches(picker) {
        if (!picker) return;
        
        const artificialSwatches = picker.querySelectorAll('.variant-picker__swatch-wrapper[data-artificial="true"]');
        artificialSwatches.forEach(swatch => swatch.remove());
    }

    /**
     * Create an artificial swatch element
     * Safety check: Verifies color doesn't exist in product before creating
     */
    /**
     * Create artificial swatch element (input + label) without wrapper
     * Used by createSwatchWithWrapper() to create template-agnostic swatches
     * @param {string} colorName - The color name from OOS_COLORS
     * @param {HTMLElement} picker - The variant picker element
     * @param {string} formId - The form ID for the input element
     * @returns {Object|null} - Object with {input, label} or null if invalid
     */
    createArtificialSwatchElement(colorName, picker, formId) {
        if (!colorName || !picker) return null;

        // Safety check: Verify color doesn't exist in the product
        const availableColors = this.getAvailableColorsFromProduct(picker);
        const colorExists = availableColors.some(availableColor =>
            this.colorsMatch(availableColor, colorName)
        );

        // If color exists in product, don't create artificial swatch
        // This prevents marking in-stock items as out of stock
        if (colorExists) {
            return null;
        }

        // Generate unique ID
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 9);

        // Create hidden radio input (disabled)
        const input = document.createElement('input');
        input.className = 'sr-only';
        input.type = 'radio';
        input.name = 'option1';
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
        input.value = colorName;
        input.id = `swatch-artificial-${colorName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}-${random}`;
        if (formId) {
            input.setAttribute('form', formId);
        }

        // Create label with disabled class - make it unclickable
        const label = document.createElement('label');
        label.className = 'color-swatch is-disabled';
        label.setAttribute('for', input.id);
        label.setAttribute('data-option-value', '');
        label.setAttribute('aria-disabled', 'true');

        // Set background color based on color name and make it unclickable
        let backgroundStyle = '';
        if (colorName === 'Wolf Grey') {
            backgroundStyle = '--swatch-background: linear-gradient(to right, #8b8b8b, #6b6b6b)';
        } else if (colorName === 'Coyote Tan') {
            backgroundStyle = '--swatch-background: url(//agilitegear.com/cdn/shop/files/coyote-tan.jpg?v=1703582706&width=72)';
        }

        // Combine background style with disabled/unclickable styles
        const fullStyle = backgroundStyle
            ? `${backgroundStyle}; pointer-events: none; cursor: not-allowed; opacity: 0.5;`
            : 'pointer-events: none; cursor: not-allowed; opacity: 0.5;';
        label.setAttribute('style', fullStyle);

        // Create sr-only span for accessibility
        const srOnly = document.createElement('span');
        srOnly.className = 'sr-only';
        srOnly.textContent = colorName;
        label.appendChild(srOnly);

        // Enhanced click/touch prevention - multiple layers
        const preventInteraction = (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
        };

        // Prevent all interactive events on both input and label
        ['click', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'touchmove'].forEach(eventType => {
            input.addEventListener(eventType, preventInteraction, true);
            label.addEventListener(eventType, preventInteraction, true);
        });

        return { input, label };
    }

    /**
     * Create artificial swatch with wrapper (template-agnostic)
     * Works with both standard and AGB variant pickers
     */
    createSwatchWithWrapper(colorName, picker, formId) {
        if (!colorName || !picker) return null;

        // Create wrapper div (works for both standard and AGB templates)
        const wrapper = document.createElement('div');
        wrapper.className = 'variant-picker__swatch-wrapper';
        wrapper.setAttribute('data-color-value', colorName);
        wrapper.setAttribute('data-artificial', 'true');

        // Generate unique ID
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 9);
        const wrapperId = `swatch-wrapper-artificial-${colorName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}-${random}`;
        wrapper.id = wrapperId;

        // Create the swatch element using refactored method
        const swatchElement = this.createArtificialSwatchElement(colorName, picker, formId);
        if (!swatchElement) return null;

        // Create few-items flag (standard structure)
        const flag = document.createElement('span');
        flag.className = 'variant-picker__few-items-flag';
        flag.style.display = 'none';

        // Assemble wrapper
        wrapper.appendChild(swatchElement.input);
        wrapper.appendChild(swatchElement.label);
        wrapper.appendChild(flag);

        return wrapper;
    }

    /**
     * Check if a swatch wrapper is out of stock (disabled/unclickable)
     * PRIORITIZES product data over DOM state to ensure accuracy
     * 
     * IMPORTANT: This method never marks in-stock items as out of stock.
     * It only returns true if NO available variant exists for the color.
     * If any variant with the color is available (available === true AND inventory_quantity > 0),
     * it returns false (not out of stock).
     */
    isSwatchOutOfStock(swatchWrapper, picker) {
        if (!swatchWrapper) return false;
        
        // Skip artificial swatches - they're always out of stock by design
        if (swatchWrapper.getAttribute('data-artificial') === 'true') {
            return true;
        }
        
        // PRIORITY 1: Check product data for variant availability FIRST
        // This is the source of truth and prevents DOM state from being wrong
        if (picker) {
            try {
                const productData = this.parseProductData(picker);
                if (productData && productData.variants) {
                    const colorValue = swatchWrapper.dataset.colorValue || swatchWrapper.dataset.swatchValue;
                    if (colorValue) {
                        // Check if any variant with this color is available
                        const hasAvailableVariant = productData.variants.some(variant => {
                            // Match by option1 (color)
                            const variantColor = variant.option1;
                            if (this.colorsMatch(variantColor, colorValue)) {
                                // Check if variant is available and has stock
                                // This ensures we never mark in-stock items as out of stock
                                return variant.available === true && (variant.inventory_quantity === null || variant.inventory_quantity > 0);
                            }
                            return false;
                        });
                        
                        // If any available variant exists for this color, it's NOT out of stock
                        // Only return true if NO available variant exists
                        if (!hasAvailableVariant) {
                            return true;
                        }
                        // If we found an available variant, return false (in stock) regardless of DOM state
                        return false;
                    }
                }
            } catch (e) {
                // Silently fail and fall back to DOM check
            }
        }
        
        // FALLBACK: Check DOM state only if product data check failed
        // Check if label has is-disabled class
        const label = swatchWrapper.querySelector('.color-swatch');
        if (label && label.classList.contains('is-disabled')) {
            return true;
        }
        
        // Check if input is disabled
        const input = swatchWrapper.querySelector('input');
        if (input && input.disabled) {
            return true;
        }
        
        // Default: not out of stock (safety - only mark as OOS if explicitly confirmed)
        return false;
    }

    /**
     * Add artificial swatches next to favorite color using OOS_COLORS list
     * Selects first two colors from OOS_COLORS that don't exist in the product
     */
    addArtificialSwatches(picker, favoriteColor) {
        if (!picker || !favoriteColor) return;

        // Get product handle
        const handle = picker.getAttribute('handle');
        if (!handle) return;

        // Check if swatch lifecycle exists and is valid
        const hasValidLifecycle = this.isSwatchLifecycleValid(handle);
        
        // If lifecycle doesn't exist or has expired, create new one
        if (!hasValidLifecycle) {
            const now = Date.now();
            if (!this.state.artificialSwatches) {
                this.state.artificialSwatches = {};
            }
            this.state.artificialSwatches[handle] = {
                addedAt: now,
                expiresAt: now + this.SWATCH_LIFECYCLE_MS // 2 weeks from now
            };
            this.saveState();
        }

        // Get available colors from the product
        const availableColors = this.getAvailableColorsFromProduct(picker);
        
        // Filter OOS_COLORS to find colors that don't exist in the product
        const colorsToAdd = (this.OOS_COLORS || []).filter(color => {
            // Check if this color exists in the product (case-insensitive match)
            return !availableColors.some(availableColor =>
                this.colorsMatch(availableColor, color)
            );
        }).slice(0, 2); // Take up to two colors that don't exist

        // Only proceed if we have at least 1 color to add
        if (colorsToAdd.length === 0) {
            return; // No colors available to add
        }

        // Remove existing artificial swatches first
        this.removeArtificialSwatches(picker);
        
        // Note: We don't fix swatch availability states here - the Liquid template handles this correctly
        // Our fixSwatchAvailabilityStates() method was causing conflicts with the template's logic

        // Find the favorite color swatch wrapper (only real swatches, not artificial)
        const favoriteSwatch = this.getSwatchWrapperForColor(picker, favoriteColor);
        if (!favoriteSwatch) return;

        // Get all REAL swatch wrappers in order (only option1 swatches, exclude artificial)
        // This ensures we work with the actual product swatches, not artificial ones
        const allSwatches = Array.from(picker.querySelectorAll('.variant-picker__swatch-wrapper')).filter(wrapper => {
            // Skip artificial swatches
            if (wrapper.getAttribute('data-artificial') === 'true') {
                return false;
            }
            const input = wrapper.querySelector('input');
            return input && input.name === 'option1';
        });

        // Find the index of the favorite color swatch in the real swatches array
        const favoriteIndex = allSwatches.indexOf(favoriteSwatch);
        if (favoriteIndex === -1) return;

        // Get form ID from existing swatches
        const existingInput = picker.querySelector('input[name="option1"]');
        const formId = existingInput?.getAttribute('form') || '';

        // Simplified positioning: Always add swatches left/right when lifecycle is active
        // Edge case handling: Don't add if favorite is at first/last position

        // LEFT: Add first color to the left (only if not at first position)
        if (favoriteIndex > 0 && colorsToAdd[0]) {
            const leftSwatchWrapper = this.createSwatchWithWrapper(colorsToAdd[0], picker, formId);
            if (leftSwatchWrapper) {
                favoriteSwatch.parentNode.insertBefore(leftSwatchWrapper, favoriteSwatch);
            }
        }

        // RIGHT: Add second color to the right (only if not at last position)
        if (favoriteIndex < allSwatches.length - 1 && colorsToAdd[1]) {
            const rightSwatchWrapper = this.createSwatchWithWrapper(colorsToAdd[1], picker, formId);
            if (rightSwatchWrapper) {
                favoriteSwatch.parentNode.insertBefore(rightSwatchWrapper, favoriteSwatch.nextSibling);
            }
        }

        // EDGE CASE: If only 1 color available and favorite is at edge position
        // Use the color on whichever side is available
        if (colorsToAdd.length === 1) {
            if (favoriteIndex === 0 && favoriteIndex < allSwatches.length - 1) {
                // At first position: only add to right
                const rightSwatchWrapper = this.createSwatchWithWrapper(colorsToAdd[0], picker, formId);
                if (rightSwatchWrapper) {
                    favoriteSwatch.parentNode.insertBefore(rightSwatchWrapper, favoriteSwatch.nextSibling);
                }
            } else if (favoriteIndex === allSwatches.length - 1 && favoriteIndex > 0) {
                // At last position: only add to left
                const leftSwatchWrapper = this.createSwatchWithWrapper(colorsToAdd[0], picker, formId);
                if (leftSwatchWrapper) {
                    favoriteSwatch.parentNode.insertBefore(leftSwatchWrapper, favoriteSwatch);
                }
            }
        }
    }

    /**
     * Show fake scarcity UI elements
     */
    showFakeScarcityUI(picker, color, count, variantId, handle) {
        const wrapper = this.getSwatchWrapperForColor(picker, color);
        if (!wrapper) return;

        // Get variant options for display
        let variantOptions = null;
        if (variantId) {
            try {
                const productData = this.parseProductData(picker);
                if (productData && productData.variants) {
                    const variant = productData.variants.find(v => String(v.id) === String(variantId));
                    if (variant) {
                        variantOptions = this.getVariantOptionsString(variant);
                    }
                }
            } catch (e) {
                // Silently fail
            }
        }

        // Add classes to swatch
        wrapper.classList.add('has-favorite-color', 'low-stock-active');

        // Show flag
        const flag = wrapper.querySelector('.variant-picker__few-items-flag');
        if (flag) {
            flag.textContent = `only ${count} left`;
            flag.style.display = 'block';
        }

        // Add border to swatch
        const swatch = wrapper.querySelector('.color-swatch') || wrapper;
        swatch.classList.add('low-stock-border');

        // Update banner with variant options
        this.updateLowStockBanner(count, variantOptions);

        this.setPdpLowStockState(handle, {
            mode: 'fake',
            variantId: variantId,
            count: count
        });

        this.trackLowStockExposure(handle, 'fake', {
            variantId: variantId ? String(variantId) : null,
            color: color || null,
            count,
            variantOptions: variantOptions || null
        });

        // Update quantity cap
        this.updateQuantityCap(count, { variantId });
    }

    /**
     * Fix swatch disabled states based on actual variant availability
     * This is a conservative fix - only corrects obvious errors, doesn't override Liquid template logic
     * REMOVED: This method was causing conflicts with the Liquid template's variant availability system
     * The Liquid template handles variant availability correctly, we should not override it
     */
    // fixSwatchAvailabilityStates() method removed - was causing conflicts

    /**
     * Clear all low stock UI elements
     */
    clearAllLowStockUI(picker) {
        const handle = picker?.getAttribute('handle');
        this.setPdpLowStockState(handle, null);

        const swatches = picker.querySelectorAll('.variant-picker__swatch-wrapper');
        swatches.forEach(wrapper => {
            wrapper.classList.remove('low-stock-active', 'has-favorite-color');
            const swatch = wrapper.querySelector('.color-swatch') || wrapper;
            swatch.classList.remove('low-stock-border');
            const flag = wrapper.querySelector('.variant-picker__few-items-flag');
            if (flag) flag.style.display = 'none';
        });
        this.clearLowStockBanner();
    }

    /**
     * Show real low stock UI for a variant (priority over fake scarcity)
     */
    showRealLowStock(picker, variantId, realStock) {
        const handle = picker?.getAttribute('handle');
        // console.log('Executing showRealLowStock:', { handle, variantId, realStock });

        // Get variant options for display
        let variantOptions = null;
        if (variantId) {
            try {
                const productData = this.parseProductData(picker);
                if (productData && productData.variants) {
                    const variant = productData.variants.find(v => String(v.id) === String(variantId));
                    if (variant) {
                        variantOptions = this.getVariantOptionsString(variant);
                    }
                }
            } catch (e) {
                // Silently fail
            }
        }

        // Update the .low-stock-variant banner div with variant options
        this.updateLowStockBanner(realStock, variantOptions);

        // Find and highlight the selected swatch
        const swatches = picker.querySelectorAll('.variant-picker__swatch-wrapper');
        let selectedWrapper = null;

        // console.log('Found swatches:', swatches.length);

        swatches.forEach(wrapper => {
            // FILTER: Only apply to Option 1 (Color) swatches
            const input = wrapper.querySelector('input');
            if (input && input.name !== 'option1') {
                return;
            }

            const isSelected =
                Boolean(wrapper.querySelector('input:checked')) ||
                wrapper.classList.contains('selected') ||
                wrapper.classList.contains('is-selected') ||
                Boolean(wrapper.querySelector('[aria-selected="true"]')) ||
                wrapper.dataset.variantId === String(variantId);

            wrapper.classList.toggle('low-stock-active', isSelected);
            wrapper.classList.toggle('has-favorite-color', isSelected);

            // Also add the red border class directly to the swatch element
            const swatch = wrapper.querySelector('.color-swatch') || wrapper;
            swatch.classList.toggle('low-stock-border', isSelected);

            const flag = wrapper.querySelector('.variant-picker__few-items-flag');
            if (flag) {
                if (isSelected) {
                    flag.textContent = `only ${realStock} left`;
                    flag.style.display = 'block';
                    selectedWrapper = wrapper;
                } else {
                    flag.style.display = 'none';
                }
            } else {
                // console.warn('Flag element not found in swatch wrapper');
            }

            if (isSelected) {
                selectedWrapper = wrapper;
            }
        });

        if (!selectedWrapper) {
            // console.warn('Could not find selected swatch wrapper to highlight');
        }

        // Update quantity cap based on real stock
        this.updateQuantityCap(realStock, { variantId });

        // Highlight the swatch border
        this.setLowStockSwatch(picker, variantId);

        this.setPdpLowStockState(handle, {
            mode: 'real',
            variantId: variantId,
            count: realStock
        });

        this.trackLowStockExposure(handle, 'real', {
            variantId: variantId ? String(variantId) : null,
            count: realStock,
            variantOptions: variantOptions || null
        });
    }

    getVariantIdForColor(picker, color) {
        try {
            const json = picker.querySelector('[data-product-variants-json]');
            const data = JSON.parse(json.textContent);
            const variant = data.variants.find(v =>
                this.colorsMatch(v.option1, color) ||
                this.colorsMatch(v.option2, color) ||
                this.colorsMatch(v.option3, color)
            );
            return variant ? variant.id : null;
        } catch (e) { return null; }
    }

    /**
     * Get real inventory quantity for a variant from product JSON
     * Returns null if not found, not available, or has no stock
     */
    getRealStockForVariant(picker, variantId) {
        try {
            const json = picker.querySelector('[data-product-variants-json]');
            const data = JSON.parse(json.textContent);
            const variant = data.variants.find(v => String(v.id) === String(variantId));

            if (variant) {
                // Check if variant is available and has stock
                if (variant.available !== true || variant.inventory_quantity <= 0) {
                    return null;
                }
                // console.log('Found variant data:', variant);
                return variant.inventory_quantity ?? null;
            } else {
                // console.warn('Variant not found in JSON:', variantId);
                return null;
            }
        } catch (e) {
            // console.error('Error getting real stock:', e);
            return null;
        }
    }


    /**
     * Get bundles module from corepack with fallback
     * Validates that the module has required methods and properties
     * Returns bundles module or null if not available
     */
    getBundlesModule() {
        let Bundles = null;

        // Try corepack first (agb-corepack.min.js)
        try {
            Bundles = window.AGB?.require?.('bundles');
        } catch (e) {
            // Corepack not available, try fallback
        }

        // Fallback to standalone bundles module (agb-bundles.js)
        if (!Bundles) {
            try {
                Bundles = window.AGB?.bundles;
            } catch (e) {
                // Fallback not available
            }
        }

        // Validate bundles module has required methods
        if (Bundles) {
            // Check for required methods
            if (typeof Bundles.isBundle !== 'function') {
                return null;
            }

            if (typeof Bundles.resolveBundleFromVariant !== 'function') {
                return null;
            }

            // Validate BUNDLE_DATA exists (optional but recommended)
            if (!Bundles.BUNDLE_DATA && !Bundles.bundleList) {
                // Continue anyway as it might be available through resolveBundleFromVariant
            }

            return Bundles;
        }

        return null;
    }

    /**
     * Get bundle product and variant data from picker for bundle resolution
     * Returns { product, variant } or null
     */
    getBundleVariant(picker) {
        try {
            const json = picker.querySelector('[data-product-variants-json]');
            if (!json) return null;

            const product = JSON.parse(json.textContent);
            const variantId = this.getSelectedVariantId(picker);
            if (!variantId || !product || !Array.isArray(product.variants)) return null;

            const variant = product.variants.find(v => String(v.id) === String(variantId));
            if (!variant) return null;

            return { product, variant };
        } catch (e) {
            return null;
        }
    }

    /**
     * Resolve bundle components from current variant selection
     * Returns array of all components (baseItem + acceptedItems) with handles, colors, and variantIds
     */
    /**
     * Resolve bundle components from BUNDLE_DATA using the bundles module
     * Extracts all component handles, variantIds, and colors from bundle resolution
     * Handles are already mapped by store-config in the bundles module (US/EU/IL)
     * Returns array of components with handle, variantId, color, and title
     */
    async resolveBundleComponents(handle, variantId) {
        try {
            // Get bundles module (from corepack with fallback)
            const Bundles = this.getBundlesModule();
            if (!Bundles) {
                return null;
            }

            // Verify this is a bundle product
            if (!Bundles.isBundle || !Bundles.isBundle(handle)) {
                return null;
            }

            // Validate BUNDLE_DATA exists (for debugging)
            if (Bundles.BUNDLE_DATA && !Bundles.BUNDLE_DATA[handle]) {
            }

            // Get picker element
            const picker = document.querySelector(`variant-picker[handle="${handle}"]`);
            if (!picker) {
                return null;
            }

            // Get bundle product and variant data
            const bundleData = this.getBundleVariant(picker);
            if (!bundleData || !bundleData.product || !bundleData.variant) {
                return null;
            }

            // Resolve bundle components using BUNDLE_DATA
            // Note: Bundles module handles store-region mapping via store-config
            // Handles returned will already be mapped for current store region (US/EU/IL)
            if (!Bundles.resolveBundleFromVariant || typeof Bundles.resolveBundleFromVariant !== 'function') {
                return null;
            }

            const result = await Bundles.resolveBundleFromVariant(bundleData.product, bundleData.variant);
            if (!result) {
                return null;
            }

            const components = [];

            // Add baseItem if exists (option1 component from BUNDLE_DATA)
            if (result.baseItem) {
                // Validate required fields
                if (!result.baseItem.handle) {
                } else if (!result.baseItem.id) {
                } else {
                    // Handle is already mapped by store-config in bundles module
                    components.push({
                        handle: result.baseItem.handle, // Already mapped for current store region
                        color: result.baseItem.color || null,
                        variantId: String(result.baseItem.id), // Ensure string format
                        title: result.baseItem.title || result.baseItem.handle
                    });
                }
            }

            // Add acceptedItems (option2+ components from BUNDLE_DATA)
            if (Array.isArray(result.acceptedItems)) {
                result.acceptedItems.forEach((item, index) => {
                    // Validate required fields
                    if (!item.handle) {
                        return;
                    }

                    const variantIdValue = item.variantId || item.id;
                    if (!variantIdValue) {
                        return;
                    }

                    // Handle is already mapped by store-config in bundles module
                    components.push({
                        handle: item.handle, // Already mapped for current store region
                        color: item.color || item.variantColor || null,
                        variantId: String(variantIdValue), // Ensure string format
                        title: item.title || item.handle
                    });
                });
            }

            // Validate we got at least one component
            if (components.length === 0) {
                return null;
            }

            // Log resolved components for debugging (only in debug mode)
            if (window.AGB_DEBUG_BUNDLES) {
            }

            return components;
        } catch (e) {
            return null;
        }
    }

    /**
     * Get low stock status for bundle baseItem (option1 component)
     * Syncs with individual product page low stock display
     * Returns { count, variantOptions, isRealStock } or null
     */
    async getBaseItemLowStock(baseItem) {
        if (!baseItem || !baseItem.handle || !baseItem.variantId) {
            return null;
        }

        // Cache for product data
        let productData = null;
        try {
            const response = await fetch(`/products/${encodeURIComponent(baseItem.handle)}.js`, {
                headers: { Accept: 'application/json' }
            });
            if (response.ok) {
                productData = await response.json();
            }
        } catch (e) {
        }

        // Check real stock first (higher priority)
        if (this.realStockEnabled && productData && Array.isArray(productData.variants)) {
            const variant = productData.variants.find(v => String(v.id) === String(baseItem.variantId));
            if (variant) {
                // Check if variant is available and has stock
                if (variant.available === true && variant.inventory_quantity > 0) {
                    const inventoryQty = variant.inventory_quantity ?? null;
                    if (inventoryQty !== null && inventoryQty <= this.REAL_STOCK_THRESHOLD && inventoryQty > 0) {
                        // Get variant options for display
                        const variantOptions = this.getVariantOptionsString(variant);
                        return {
                            count: inventoryQty,
                            variantOptions: variantOptions,
                            isRealStock: true
                        };
                    }
                }
            }
        }

        // Check fake scarcity (variant-specific)
        if (this.uiEnabled && baseItem.variantId && productData && Array.isArray(productData.variants)) {
            const variant = productData.variants.find(v => String(v.id) === String(baseItem.variantId));
            // Only check fake scarcity if variant is available and has stock
            if (variant && variant.available === true && variant.inventory_quantity > 0) {
                const status = this.getScarcityStatus(baseItem.handle, baseItem.variantId);
                if (status && status.count !== null && status.count > 0) {
                    return {
                        count: status.count,
                        variantOptions: status.variantOptions || baseItem.color || '',
                        isRealStock: false
                    };
                }
            }
        }

        return null;
    }

    /**
     * Check low stock for all bundle components (both fake scarcity and real stock)
     * Uses correct variantIds from BUNDLE_DATA resolution
     * Returns the lowest stock count found, or null if no low stock
     */
    async checkBundleComponentsLowStock(components) {
        if (!components || !Array.isArray(components) || components.length === 0) {
            return null;
        }

        let lowestCount = null;
        let hasLowStock = false;

        // Cache for product data to avoid redundant fetches
        const productCache = new Map();

        // Helper to fetch product data with error handling
        const fetchProductData = async (handle) => {
            if (!handle) return null;

            if (productCache.has(handle)) {
                return productCache.get(handle);
            }
            try {
                const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
                    headers: { Accept: 'application/json' }
                });
                if (!response.ok) {
                    return null;
                }
                const data = await response.json();
                productCache.set(handle, data);
                return data;
            } catch (e) {
                return null;
            }
        };

        // Helper to get real stock for a variant
        const getRealStockForComponent = async (component) => {
            // Validate component has required fields
            if (!component || !component.handle || !component.variantId) {
                return null;
            }

            // Check if real stock checking is enabled
            if (!this.realStockEnabled) {
                return null;
            }

            // Fetch product data for this component
            const productData = await fetchProductData(component.handle);
            if (!productData || !Array.isArray(productData.variants)) {
                return null;
            }

            // Find the specific variant by variantId
            const variant = productData.variants.find(v => String(v.id) === String(component.variantId));
            if (!variant) {
                return null;
            }

            // Check if variant is available and has stock
            if (variant.available !== true || variant.inventory_quantity <= 0) {
                return null;
            }

            // Check inventory quantity
            const inventoryQty = variant.inventory_quantity ?? null;
            if (inventoryQty !== null && inventoryQty <= this.REAL_STOCK_THRESHOLD && inventoryQty > 0) {
                return inventoryQty;
            }

            return null;
        };

        // Check each component for low stock
        for (const component of components) {
            // Validate component structure
            if (!component || typeof component !== 'object') {
                continue;
            }

            if (!component.handle || !component.variantId) {
                continue;
            }

            // Check real stock first (higher priority than fake scarcity)
            const realStock = await getRealStockForComponent(component);
            if (realStock !== null) {
                hasLowStock = true;
                if (lowestCount === null || realStock < lowestCount) {
                    lowestCount = realStock;
                }
                // Real stock takes priority, skip fake scarcity check for this component
                continue;
            }

            // Check fake scarcity - variant-specific
            if (this.uiEnabled && component.variantId) {
                try {
                    // Verify variant is available before checking fake scarcity
                    const productData = await fetchProductData(component.handle);
                    if (productData && Array.isArray(productData.variants)) {
                        const variant = productData.variants.find(v => String(v.id) === String(component.variantId));
                        // Only check fake scarcity if variant is available and has stock
                        if (variant && variant.available === true && variant.inventory_quantity > 0) {
                            // Get variant-specific scarcity status
                            const status = this.getScarcityStatus(component.handle, component.variantId);
                            if (status && status.count !== null && status.count > 0) {
                                hasLowStock = true;
                                if (lowestCount === null || status.count < lowestCount) {
                                    lowestCount = status.count;
                                }
                            }
                        }
                    }
                } catch (e) {
                }
            }
        }

        // Return lowest count if any component has low stock, otherwise null
        return hasLowStock ? lowestCount : null;
    }

    /**
     * Get currently selected variant ID from the picker or page
     */
    getSelectedVariantId(picker) {
        // Try hidden input inside picker first
        const pickerInput = picker?.querySelector('input[name="id"]');
        if (pickerInput?.value) return pickerInput.value;

        // Try hidden input in product form (outside picker)
        const formInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (formInput?.value) return formInput.value;

        // Try checked radio/checkbox inside picker
        const checkedInput = picker?.querySelector('input:checked');
        if (checkedInput?.value) return checkedInput.value;

        // Try data attribute on selected swatch
        const selectedSwatch = picker?.querySelector('.variant-picker__swatch-wrapper.is-selected, .variant-picker__swatch-wrapper.selected');
        if (selectedSwatch?.dataset?.variantId) return selectedSwatch.dataset.variantId;

        return null;
    }

    getLowStockTrackingPropertyNames() {
        return [
            '_isLowStockOnAddToCart',
            '_leftOnAddToCart',
            '_leftOnCheckout',
            '_minuteToCheckoutFromCart',
            '_lowStockVariantId',
            '_lowStockHandle',
            '_lowStockAddedAtMs'
        ];
    }

    getDeprecatedLowStockPropertyNames() {
        return [
            '_LowStockCount',
            '_LowStockVariantId',
            '_LowStockHandle',
            '_LowStockDescription',
            '_LowStockType',
            '_LowStockPhase',
            '_LowStockVariantOptions',
            '_LowStockLifecycleStartAt',
            '_LowStockAddedAt',
            '_LowStockExposureSec',
            '_LowStockSessionId',
            '_LowStockSource'
        ];
    }

    removeLowStockTrackingInputs(form) {
        if (!form) return;

        const allPropertyNames = [
            ...this.getLowStockTrackingPropertyNames(),
            ...this.getDeprecatedLowStockPropertyNames()
        ];

        allPropertyNames.forEach(propName => {
            const inputs = form.querySelectorAll(`input[name="properties[${propName}]"]`);
            inputs.forEach(input => input.remove());
        });
    }

    upsertHiddenPropertyInput(form, propName, propValue) {
        if (!form || !propName) return;
        if (propValue == null || String(propValue).trim() === '') return;

        let input = form.querySelector(`input[name="properties[${propName}]"]`);
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${propName}]`;
            form.appendChild(input);
        }
        input.value = String(propValue);
    }

    applyLowStockTrackingPropertiesToForm(form, properties) {
        if (!form) return;

        this.removeLowStockTrackingInputs(form);
        if (!properties || typeof properties !== 'object') return;

        Object.entries(properties).forEach(([propName, propValue]) => {
            this.upsertHiddenPropertyInput(form, propName, propValue);
        });
    }

    getSelectedSwatchWrapper(picker) {
        if (!picker) return null;

        const wrappers = picker.querySelectorAll('.variant-picker__swatch-wrapper');
        for (const wrapper of wrappers) {
            if (wrapper.querySelector('input:checked')) {
                return wrapper;
            }
        }

        return picker.querySelector('.variant-picker__swatch-wrapper.is-selected, .variant-picker__swatch-wrapper.selected');
    }

    setPdpLowStockState(handle, state) {
        if (!handle) return;
        if (!state) {
            delete this.pdpLowStockState[handle];
            return;
        }
        this.pdpLowStockState[handle] = {
            ...state,
            updatedAt: Date.now()
        };
    }

    getActiveLowStockTrackingContextForForm(form) {
        if (!this.uiEnabled) return null;

        const picker = form.closest('variant-picker') || document.querySelector('variant-picker');
        if (!picker) return null;

        const handle = picker.getAttribute('handle');
        if (!handle) return null;

        const variantId = this.getSelectedVariantId(picker);
        if (!variantId) return null;

        const status = this.getScarcityStatus(handle, variantId);
        if (!status || status.count == null || status.count <= 0) return null;

        const lifecycle = this.getLifecycleState(handle, variantId);
        if (!lifecycle || this.isLifecycleExpired(lifecycle)) return null;

        const pdpState = this.pdpLowStockState[handle];
        if (!pdpState || pdpState.mode !== 'fake') return null;
        if (String(pdpState.variantId) !== String(variantId)) return null;

        const selectedWrapper = this.getSelectedSwatchWrapper(picker);
        if (!selectedWrapper || !selectedWrapper.classList.contains('low-stock-active')) return null;

        const flag = selectedWrapper.querySelector('.variant-picker__few-items-flag');
        const flagIsVisible = Boolean(flag && flag.style.display !== 'none');
        if (!flagIsVisible) return null;

        const displayedCount = this.toPositiveInt((flag.textContent || '').replace(/\D+/g, ''));
        const leftOnAddToCart = displayedCount ?? status.count;

        return {
            handle,
            variantId,
            count: leftOnAddToCart
        };
    }

    updateLineQuantityLimit(lineKey, limit) {
        if (!lineKey || !Number.isFinite(limit)) return;

        // Find all inputs with this line key (there might be multiple in different contexts)
        const inputs = document.querySelectorAll(`input[data-line-key="${lineKey}"]`);
        if (inputs.length === 0) return;

        // console.log('Limiting line', lineKey, 'to', limit);
        const resolvedLimit = Math.max(1, Math.min(69, Math.max(0, limit)));
        this.lineLimits[lineKey] = resolvedLimit;

        // Update all inputs with this line key
        inputs.forEach(input => {
            // Update Input Attributes - these are critical for HTML5 validation
            input.setAttribute('max', resolvedLimit);
            input.dataset.maxStock = resolvedLimit;

            // Enforce value limit immediately
            const currentValue = parseInt(input.value, 10);
            if (!isNaN(currentValue) && currentValue > resolvedLimit) {
                // console.log(`[ScarcityEngine] Enforcing limit of ${resolvedLimit} for ${lineKey}. Updating cart...`);
                input.value = resolvedLimit;
                // Trigger change event to update cart with clamped value
                input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
            }

            // Handle Plus Button State
            // The plus button is usually the next sibling or inside the same container
            // Based on snippet: <a ... class="quantity-selector__button">...plus icon...</a>
            // It is the NEXT element sibling of the input in the snippet structure
            const plusBtn = input.nextElementSibling;

            if (plusBtn && plusBtn.classList.contains('quantity-selector__button')) {
                const currentVal = parseInt(input.value) || 0;

                if (currentVal >= resolvedLimit) {
                    plusBtn.classList.add('disabled');
                    plusBtn.setAttribute('disabled', 'disabled');
                    plusBtn.style.pointerEvents = 'none';
                    plusBtn.style.opacity = '0.5';
                    plusBtn.setAttribute('aria-disabled', 'true');

                    // Update plus button href to prevent navigation over limit
                    if (plusBtn.tagName === 'A' && plusBtn.href) {
                        const url = new URL(plusBtn.href);
                        url.searchParams.set('quantity', String(resolvedLimit));
                        plusBtn.href = url.toString();
                    }
                } else {
                    plusBtn.classList.remove('disabled');
                    plusBtn.removeAttribute('disabled');
                    plusBtn.style.pointerEvents = '';
                    plusBtn.style.opacity = '';
                    plusBtn.removeAttribute('aria-disabled');
                }
            }
        });
    }

    updateLowStockBanner(count, variantOptions = null) {
        const banner = document.querySelector('.low-stock-variant');
        if (!banner) return;
        if (count == null) {
            banner.innerHTML = '';
            banner.classList.remove('is-active');
            banner.style.display = 'none';
            return;
        }

        // Build message with variant options if provided
        let message = `only ${count} items left in stock`;
        if (variantOptions && variantOptions.trim()) {
            message += ` - ${variantOptions}`;
        }

        banner.innerHTML = `<p class="text-red-600 text-sm uppercase tracking-wide font-semibold">${message}</p>`;
        banner.classList.add('is-active');
        banner.style.display = '';
    }

    clearLowStockBanner() {
        this.updateLowStockBanner(null);
        this.clearLowStockSwatch();
    }

    setLowStockSwatch(picker, variantId) {
        const colorSwatches = document.querySelectorAll('.color-swatch, .variant-picker__swatch-wrapper');
        colorSwatches.forEach(el => el.classList.remove('low-stock-border'));

        if (!variantId) return;

        const targetInput = picker.querySelector(`[value="${variantId}"], input[data-variant-id="${variantId}"]`);
        const wrapper = targetInput?.closest('.variant-picker__swatch-wrapper');
        const swatch = wrapper?.querySelector('.color-swatch') || wrapper;
        if (swatch) {
            swatch.classList.add('low-stock-border');
        }
    }

    clearLowStockSwatch() {
        document.querySelectorAll('.color-swatch.low-stock-border').forEach(el => el.classList.remove('low-stock-border'));
    }

    isFavoriteActive(picker, variantId) {
        if (!picker || !variantId) return false;
        const input = picker.querySelector(`[value="${variantId}"]`) || picker.querySelector(`[data-variant-id="${variantId}"]`);
        if (!input) return false;
        const wrapper = input.closest('.variant-picker__swatch-wrapper');
        return Boolean(
            wrapper?.querySelector('input:checked') ||
            wrapper?.classList.contains('selected') ||
            wrapper?.classList.contains('is-selected')
        );
    }

    updateQuantityCap(count, options = {}) {
        if (count == null) return;
        const { variantId, lineKey } = options;
        let selector = 'quantity-selector input';
        if (lineKey) {
            selector = `quantity-selector input[data-line-key="${lineKey}"]`;
        } else if (variantId) {
            selector = `quantity-selector input[data-variant-id="${variantId}"]`;
        }

        const inputs = document.querySelectorAll(selector);
        inputs.forEach(input => {
            input.setAttribute('max', count);
            input.setAttribute('data-max-stock', count);
            if (parseInt(input.value) > count) input.value = count;
        });
    }

    initUI() {
        // Helper to refresh if multiple pickers exist
        const pickers = document.querySelectorAll('variant-picker');
        pickers.forEach(p => this.refreshUI(p.getAttribute('handle')));

        // Always try refreshing cart items (they might exist without a picker)
        this.refreshCartItems();
    }

    startDecayTimer() {
        setInterval(() => {
            this.initUI();
        }, 5000);
    }
}

// Inject low-stock border styles
(function injectScarcityStyles() {
    if (document.getElementById('scarcity-engine-styles')) return;
    const style = document.createElement('style');
    style.id = 'scarcity-engine-styles';
    style.textContent = `
        .variant-picker__swatch-wrapper.low-stock-active .color-swatch,
        .color-swatch.low-stock-border {
            border-color: #d32f2f !important;
            box-shadow: 0 0 0 2px rgba(211, 47, 47, .45) !important;
        }
        .low-stock-variant {
            min-height: 24px;
        }
        .low-stock-variant.is-active {
            color: #d32f2f;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.025em;
            text-align: center;
            padding: 4px 8px;
            border-radius: 3px;
        }
    `;
    document.head.appendChild(style);
})();

// Initialize
window.ScarcityEngine = new ScarcityEngine();

// Dev Helpers (exposed globally)
window.ScarcityDev = {
    // Toggle artificial swatches system on/off
    toggleArtificialSwatches: (enabled = null) => {
        if (!window.ScarcityEngine) return;
        
        if (enabled === null) {
            // Toggle current state
            window.ScarcityEngine.artificialSwatchesEnabled = !window.ScarcityEngine.artificialSwatchesEnabled;
        } else {
            // Set to specific state
            window.ScarcityEngine.artificialSwatchesEnabled = Boolean(enabled);
        }
        
        // Refresh UI to apply changes
        const picker = document.querySelector('variant-picker');
        if (picker) {
            const handle = picker.getAttribute('handle');
            if (handle) {
                window.ScarcityEngine.refreshUI(handle);
            }
        }
        
        return window.ScarcityEngine.artificialSwatchesEnabled;
    },
    
    // Get current artificial swatches state
    getArtificialSwatchesEnabled: () => {
        return window.ScarcityEngine?.artificialSwatchesEnabled ?? false;
    },
    
    // Manually set low stock for current product color
    setLowStock: (count = 3) => {
        if (!window.ScarcityEngine) return;

        const picker = document.querySelector('variant-picker');
        if (!picker) {
            // console.warn('No variant picker found');
            return;
        }

        const handle = picker.getAttribute('handle');
        // Get currently selected color
        const checkedInput = picker.querySelector('input:checked');
        const wrapper = checkedInput?.closest('.variant-picker__swatch-wrapper');
        const color = wrapper?.dataset.colorValue;

        if (!handle || !color) {
            // console.warn('Could not determine handle or color');
            return;
        }

        // console.log(`Forcing low stock (${count}) for ${handle} - ${color}`);

        // Force lifecycle creation/update with new decay schedule
        const firstDropMs = (Math.floor(Math.random() * 26) + 15) * 1000; // 15-40 seconds
        const secondDropMs = (Math.floor(Math.random() * 26) + 15) * 60 * 1000; // 15-40 minutes

        window.ScarcityEngine.state.lifecycles = window.ScarcityEngine.state.lifecycles || {};
        window.ScarcityEngine.state.lifecycles[handle] = {
            startTime: Date.now(),
            activeColor: color,
            initialCount: count || (Math.floor(Math.random() * 2) + 8), // Use provided count or default to 8-9
            firstDropMs: firstDropMs,
            secondDropMs: secondDropMs
        };
        window.ScarcityEngine.saveState();
        window.ScarcityEngine.refreshUI(handle);
    },

    // Clear all scarcity data
    reset: () => {
        localStorage.removeItem('AGB_ScarcityState');
        window.location.reload();
    },

    // Print current state
    debug: () => {
        // console.table(window.ScarcityEngine.state.lifecycles);
        // console.log('Full State:', window.ScarcityEngine.state);
    },

    // Inspect scarcity tracking transport/queue status
    trackingStats: () => {
        if (!window.ScarcityEngine) return null;
        return window.ScarcityEngine.getTrackingStats();
    },

    // Inspect queued tracking events
    trackingQueue: () => {
        if (!window.ScarcityEngine) return [];
        return window.ScarcityEngine.loadTrackingQueue();
    },

    // Set tracking endpoint at runtime
    setTrackingEndpoint: (url = '') => {
        window.AGB_SCARCITY_TRACKING_ENDPOINT = String(url || '').trim();
        if (window.ScarcityEngine) {
            window.ScarcityEngine.TRACKING_ENDPOINT = window.AGB_SCARCITY_TRACKING_ENDPOINT;
        }
        return window.AGB_SCARCITY_TRACKING_ENDPOINT;
    },

    // Force a manual queue flush
    flushTrackingNow: () => {
        if (!window.ScarcityEngine) return Promise.resolve(false);
        return window.ScarcityEngine.flushTrackingQueue({ force: true });
    },

    // Manual test function for patch promotion
    testPatchPromotion: async () => {
        if (window.ScarcityEngine) {
            // Clear existing promotion state for testing
            window.ScarcityEngine.state.freePatchPromotion = null;
            window.ScarcityEngine.saveState();
            // Trigger promotion
            await window.ScarcityEngine.triggerFreePatchPromotion();
        } else {
        }
    }
};

// Compatibility Shim for Legacy Scripts
window.trackProductVisit = function (handle, color, interactionType, selectionType) {
    if (window.ScarcityEngine) {
        // Map legacy interaction types to new priority system
        let type = 'load'; // Default

        if (interactionType === 'addedToCart') {
            type = 'cart';
        } else if (selectionType === 'active') {
            type = 'click';
        }

        window.ScarcityEngine.recordInteraction(handle, color, type);
        window.ScarcityEngine.refreshUI(handle);
    }
};

window.getFavoriteColor = function () {
    // This is a best-effort shim; getting favorite color usually requires a handle in the new system.
    // We'll try to find the first product handle on the page or return null.
    const picker = document.querySelector('variant-picker');
    const handle = picker ? picker.getAttribute('handle') : null;

    if (window.ScarcityEngine && handle) {
        return window.ScarcityEngine.getBestColor(handle);
    }
    return null;
};
